import {
  CollisionDetection,
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  pointerWithin,
  rectIntersection,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { CloseRounded, DeleteOutline as DeleteOutlineIcon, EditOutlined, RestartAltRounded, SwapHorizRounded } from '@mui/icons-material';
import CallSplitRoundedIcon from '@mui/icons-material/CallSplitRounded';
import { Backdrop, Box, Button, CircularProgress, Dialog, IconButton, MenuItem, Popover, TextField, Tooltip, Typography } from '@mui/material';
import { InfiniteData, UseInfiniteQueryResult, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import { AxiosError, AxiosResponse } from 'axios';
import { useSnackbar } from 'notistack';
import { FC, KeyboardEvent, memo, MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactGA from 'react-ga4';
import { Helmet } from 'react-helmet-async';
import { ConfirmDialog } from 'src/components/ConfirmDialog';
import { ReportPrintingDialogs, useReportPrinting } from 'src/components/ReportPrinting';
import { ShipmentDetail } from 'src/components/shipment/ShipmentDetail';
import ShipmentOrderLegRunSplitFormPresenter from 'src/components/ShipmentOrderLegRunSplitForm/Presenter';
import { appBarHeight } from 'src/constants/layout';
import { SCREEN_NAMES } from 'src/constants/screenNames';
import {
  ConvenienceDispatchFixedRouteDetailEntity,
  ConvenienceDispatchFixedRouteEntity,
} from 'src/entities/ConvenienceDispatchFixedRoute.entity';
import { DeliveryCard, DispatchCard, LoadingCard, UnassignedItem, Vehicle } from 'src/entities/dispatch';
import { PlanningEntity } from 'src/entities/planningEntity';
import { PlanningSettingRequest } from 'src/entities/PlanningHistory.entity';
import { AsyncPlanningResponseEntity, PlanningResponseEntity } from 'src/entities/planningResponseEntity';
import { PlanningsDeliveryEntity, PlanningsDeliveryWithLegRunEntity } from 'src/entities/PlanningsDelivery.entity';
import { PlanningsDriverEntity } from 'src/entities/PlanningsDriver.entity';
import { PlanningsGroupEntity } from 'src/entities/PlanningsGroup.entity';
import { PlanningsNotAllocReason } from 'src/entities/PlanningsNotAllocReasons.entity';
import { PlanningsTruckEntity } from 'src/entities/PlanningsTruck.entity';
import { ShipmentOrderLegRunForPlanEntity, ShipmentOrderLegRunListResponse } from 'src/entities/ShipmentOrderLegRun.entity';
import { TimelineRequestEntity } from 'src/entities/TimelineRequest.entity';
import { useBulkUpdateWaypointLocationMutation } from 'src/hooks/useBulkUpdateWaypointLocation.mutation';
import {
  CONVENIENCE_DISPATCH_ALL_DELIVERIES_QUERY_KEY,
  CONVENIENCE_DISPATCH_DELIVERIES_QUERY_KEY,
} from 'src/hooks/useConvenienceDispatchDeliveries.query';
import { useConvenienceDispatchShiftTruckMutation } from 'src/hooks/useConvenienceDispatchShiftTruck.mutation';
import { usePersistedJSON } from 'src/hooks/usePersistedJSON';
import { useQueryLatestAlgorithmRequestVersion } from 'src/hooks/useQueryLatestAlgorithmRequestVersion';
import { useMutationPlanningV5 } from 'src/hooks/useQueryPlanning';
import { useQueryPlanningGroups } from 'src/hooks/useQueryPlanningGroups';
import { useQueryPlanningsNotAllocReasons } from 'src/hooks/useQueryPlanningsNotAllocReasons';
import { useQueryPlanningsResultsV5 } from 'src/hooks/useQueryPlanningsResults';
import { clearRunningPlanningsCache, invalidateRunningPlanningsCache, useMutationPlanningRunnings, useQueryPlanningsRunnings } from 'src/hooks/useQueryPlanningsRunnings';
import { useQueryPlanningsSuggestions } from 'src/hooks/useQueryPlanningsSuggestions';
import { useMutationPlanningTruckDisplayOrders } from 'src/hooks/useQueryPlanningsTrucks';
import { SHIPMENT_ORDER_LEG_RUNS_QUERY_KEY } from 'src/hooks/useQueryShipmentOrderLegRuns';
import { useMutationShipmentOrder } from 'src/hooks/useQueryShipmentOrders';
import { useMutationShipmentOrderLegRun } from 'src/hooks/useShipmentOrderLegRun';
import { useUpdateShiftTruckTruckNumberMutation } from 'src/hooks/useUpdateShiftTruckTruckNumber.mutation';
import { debounce } from 'src/utils/debounce';
import { compareTrucksByDisplayOrder } from 'src/utils/truckDisplayOrder.util';
import { RelaxedRuleValues, RelaxedRuleVo } from 'src/vo/RelaxedRule.vo';
import { TruckDisplayStatusVo } from 'src/vo/TruckDisplayStatusVo';


import LaneDetailSheet from '../V2ShipmentOrderLanes/presenters/LaneDetailSheet';
import NotAllocSuggestionComponent from '../V3Plans/components/NotAllocSuggestion.component';
import PlanningPresenter from '../V3Plans/presenters/PlanningPresenter';

import {
  buildVehicleRowGroups,
  filterHiddenPlaceholderRotations,
  getMatchedLegRunIdsInRow,
  rowIdFromTruck,
  toUnassignedItem,
  VehicleInfoExtra,
  VehicleRowGroup,
} from './adapter';
import {
  shouldClearOverrideAfterPlanningResult,
  shouldResetOverrideAfterServerRefresh,
} from './autoDispatchQueue';
import ConvenienceDispatchAddVehicleDialog from './ConvenienceDispatchAddVehicleDialog';
import ConvenienceDispatchBoard from './ConvenienceDispatchBoard';
import ConvenienceDispatchToolbar from './ConvenienceDispatchToolbar';
import ConvenienceDispatchUnassignedList from './ConvenienceDispatchUnassignedList';
import ConvenienceDispatchVehicleList from './ConvenienceDispatchVehicleList';
import { DispatchCardView } from './DispatchCardView';
import { DispatchDetailPanel, DispatchSelection } from './DispatchDetailPanel';
import { DisplayMetric } from './DisplayMetricToggle';
import DriverSelectMenu from './DriverSelectMenu';
import TruckSelectMenu from './TruckSelectMenu';
import { buildVehicleRows, VehicleRowVM } from './vehicleRowVM';

import type { GeoAlertInfo } from 'src/utils/geoAlerts';

type DeliveriesResponse = {
  ids: number[];
  total_pages_count: number;
  data: PlanningsDeliveryWithLegRunEntity[];
};

// localStorage 復元時のバリデータ（usePersistedJSON 用）。DisplayMetric 列挙の追加で
// 過去の保存値が古いキーになっていてもここで弾いて初期値に戻す。
const VALID_DISPLAY_METRICS: ReadonlySet<DisplayMetric> = new Set<DisplayMetric>([
  'quantity', 'weight', 'volume',
  'origin_dest', 'section_pref', 'section_city', 'address', 'shipper', 'time_window', 'conditions',
]);

function isDisplayMetricArray(parsed: unknown): parsed is DisplayMetric[] {
  return Array.isArray(parsed)
    && parsed.every((m): m is DisplayMetric => typeof m === 'string' && VALID_DISPLAY_METRICS.has(m as DisplayMetric));
}

function isPositiveInteger(parsed: unknown): parsed is number {
  return typeof parsed === 'number' && Number.isInteger(parsed) && parsed > 0;
}

// 楽観的 UI 更新用の Vehicle 表示フィールド override。truckId 単位でこの値を被せる。
type VehicleDisplayOverride = { truckName?: string; plateNumber?: string; driverName?: string };

// V3Plans の TruckSelectMenuPresenter と同じ Vo を共有してメニュー UI を揃える。
const TRUCK_DISPLAY_STATUS_VALUES: ReadonlySet<TruckDisplayStatusVo> = new Set<TruckDisplayStatusVo>([
  'すべて表示', '配車済み車両のみ表示', '未配車の車両のみ表示', '選択中のみ表示する',
]);

function isTruckDisplayStatus(parsed: unknown): parsed is TruckDisplayStatusVo {
  return typeof parsed === 'string' && TRUCK_DISPLAY_STATUS_VALUES.has(parsed as TruckDisplayStatusVo);
}

function isNumberArray(parsed: unknown): parsed is number[] {
  return Array.isArray(parsed) && parsed.every((x) => typeof x === 'number');
}

function loadingPlaceKey(lr: ShipmentOrderLegRunForPlanEntity): string {
  if (lr.loading_lat != null && lr.loading_lng != null) return `${lr.loading_lat}-${lr.loading_lng}`;
  return lr.loading_name || `lr-${lr.id ?? 'x'}-load`;
}

function unloadingPlaceKey(lr: ShipmentOrderLegRunForPlanEntity): string {
  if (lr.unloading_lat != null && lr.unloading_lng != null) return `${lr.unloading_lat}-${lr.unloading_lng}`;
  return lr.unloading_name || `lr-${lr.id ?? 'x'}-unload`;
}

// 地点間の想定移動時間（秒）。実距離は考慮せず、仕様上の固定値。
const TRAVEL_SECONDS_BETWEEN_PLACES = 30 * 60;

function addSecondsToDate(date: Date, seconds: number): Date {
  return new Date(date.getTime() + Math.max(0, seconds) * 1000);
}

/** ISO 形式 / Date 文字列を "HH:mm" に整形する。失敗時は null。 */
function formatHourMinute(value: string | undefined | null): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const hh = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

/**
 * 1 cycle 分の legRuns に対し、積→降 の arrival_at/departure_at を生成する。
 *
 * 仕様:
 * - baseline は呼び出し側で算出して渡す (前 cycle の最終 departure + 30分 / shift 開始時刻 等)
 * - 積み: loading 地点 (placeKey) ごとに集約。同地点の複数 legRun は同一 arrival/departure
 * - 別地点の積み: 前地点 departure + 30分 → 当地点 arrival
 * - 降り 1件目: 最後の積み departure + 30分、以降は積みと同じ規則
 *
 * 時間は配車アルゴリズムで再計算されるため正確である必要はなく、並びの整合性のみが重要。
 */
function computeOpsForCycle(args: {
  stid: number;
  legRuns: ShipmentOrderLegRunForPlanEntity[];
  cycle: number;
  baseline: Date;
}): {
  shift_truck_id: number;
  order_id: number;
  arrival_at: Date;
  departure_at: Date;
  action: '積' | '降';
  cycle: number;
}[] {
  const { stid, legRuns: groupLegRuns, cycle, baseline } = args;
  if (groupLegRuns.length === 0) return [];

  const result: {
    shift_truck_id: number;
    order_id: number;
    arrival_at: Date;
    departure_at: Date;
    action: '積' | '降';
    cycle: number;
  }[] = [];

  // 積みを placeKey ごとに集約 (出現順維持)
  const loadingGroups: ShipmentOrderLegRunForPlanEntity[][] = [];
  const loadingPlaceIdx = new Map<string, number>();
  groupLegRuns.forEach((lr) => {
    const key = loadingPlaceKey(lr);
    const idx = loadingPlaceIdx.get(key);
    if (idx != null) loadingGroups[idx].push(lr);
    else {
      loadingPlaceIdx.set(key, loadingGroups.length);
      loadingGroups.push([lr]);
    }
  });

  let lastDeparture = baseline;
  loadingGroups.forEach((group, groupIdx) => {
    const arrival = groupIdx === 0
      ? baseline
      : addSecondsToDate(lastDeparture, TRAVEL_SECONDS_BETWEEN_PLACES);
    const maxStaying = group.reduce((max, lr) => Math.max(max, lr.loading_staying_seconds || 0), 0);
    const departure = addSecondsToDate(arrival, maxStaying);
    group.forEach((lr, i) => {
      if (lr.id == null) return;
      result.push({
        shift_truck_id: stid,
        order_id: lr.id,
        // 同一地点 2件目以降は 1件目の departure_at に揃える (到着後すぐ出発扱い)
        arrival_at: i === 0 ? arrival : departure,
        departure_at: departure,
        action: '積',
        cycle,
      });
    });
    lastDeparture = departure;
  });

  // 降りを placeKey ごとに集約
  const unloadingGroups: ShipmentOrderLegRunForPlanEntity[][] = [];
  const unloadingPlaceIdx = new Map<string, number>();
  groupLegRuns.forEach((lr) => {
    const key = unloadingPlaceKey(lr);
    const idx = unloadingPlaceIdx.get(key);
    if (idx != null) unloadingGroups[idx].push(lr);
    else {
      unloadingPlaceIdx.set(key, unloadingGroups.length);
      unloadingGroups.push([lr]);
    }
  });

  unloadingGroups.forEach((group) => {
    const arrival = addSecondsToDate(lastDeparture, TRAVEL_SECONDS_BETWEEN_PLACES);
    const maxStaying = group.reduce((max, lr) => Math.max(max, lr.unloading_staying_seconds || 0), 0);
    const departure = addSecondsToDate(arrival, maxStaying);
    group.forEach((lr, i) => {
      if (lr.id == null) return;
      result.push({
        shift_truck_id: stid,
        order_id: lr.id,
        arrival_at: i === 0 ? arrival : departure,
        departure_at: departure,
        action: '降',
        cycle,
      });
    });
    lastDeparture = departure;
  });

  return result;
}

/**
 * 行の cards 配列に legRun 1 件を配置する。
 * - 同じ loading 地点の LoadingCard があれば merge（legRunIds のみ追加）、無ければ先頭 loading セクションに挿入
 * - 同じ unloading 地点の DeliveryCard があれば merge（数量・重量加算 + legRunIds 追加）、無ければ指定位置に挿入
 */
function placeLegRunInRow(
  cards: DispatchCard[],
  legRun: ShipmentOrderLegRunForPlanEntity,
  rotationNumber: number,
  preferredInsertAt: number,
  truckId?: number,
): DispatchCard[] {
  const legRunId = legRun.id;
  if (legRunId == null) return cards;

  const next = [...cards];
  let insertAt = preferredInsertAt;

  // 0a) 固定ルート土台で作られた降地カード (frStopType='unloading' の fr-template) のうち、
  //     ドロップ対象 legRun を候補に含むものは「土台 → 本物の案件」へリプレイスする。
  //     fr-template の placeKey は `unloading-<name>-<lat>-<lng>` 形式で、後段 uKey 検索
  //     (`<lat>-<lng>`) では hit しないため、ここで legRunIds ベースに別途探す。
  //     リプレイスは len 不変 in-place 更新で、loading 側の処理は通常どおり継続させるため
  //     ここでは return せず、後段の alreadyPlacedIdx で早期 return させる。
  const frTemplateUnloadingIdx = next.findIndex(
    (c) => c.type === 'delivery'
      && c.status === 'fr-template'
      && c.frStopType === 'unloading'
      && (c.legRunIds ?? []).includes(legRunId),
  );
  if (frTemplateUnloadingIdx >= 0) {
    const existing = next[frTemplateUnloadingIdx] as DeliveryCard;
    next[frTemplateUnloadingIdx] = {
      ...existing,
      cases: legRun.item_count ?? 0,
      todayCases: legRun.item_count ?? 0,
      weight: legRun.item_total_weight_kg ?? null,
      volume: legRun.item_total_volume_m3 ?? null,
      status: 'template',
      legRunIds: [legRunId],
      origin: legRun.loading_name,
      destination: legRun.unloading_name,
      shippers: legRun.shipper_name ? [legRun.shipper_name] : undefined,
      laneId: legRun.shipment_order_lane_id ?? null,
      frStopType: undefined,
      placeKey: unloadingPlaceKey(legRun),
    };
  }

  // 0b) 固定ルート土台で作られた積地系カード (frStopType='loading' の template / no-delivery) を
  //    同一 storeName で除去する。ドロップした legRun の積地カードと重複表示させないため。
  const frLoadingTemplateIdx = next.findIndex(
    (c) => c.type === 'delivery'
      && (c.status === 'fr-template' || c.status === 'no-delivery')
      && c.frStopType === 'loading'
      && c.storeName === legRun.loading_name,
  );
  if (frLoadingTemplateIdx >= 0) {
    next.splice(frLoadingTemplateIdx, 1);
    if (frLoadingTemplateIdx <= insertAt) insertAt = Math.max(0, insertAt - 1);
  }

  // 1) Loading
  const lKey = loadingPlaceKey(legRun);
  const loadingIdx = next.findIndex((c) => c.type === 'loading' && c.placeKey === lKey);
  if (loadingIdx >= 0) {
    const existing = next[loadingIdx] as LoadingCard;
    if (!(existing.legRunIds ?? []).includes(legRunId)) {
      next[loadingIdx] = { ...existing, legRunIds: [...(existing.legRunIds ?? []), legRunId] };
    }
  } else {
    // 回転追加で置かれた「再積み込み拠点」placeholder (placeKey が `reload-` 始まり) は
    // 実際の積地が確定した時点で不要になるので除去する
    const placeholderIdx = next.findIndex(
      (c) => c.type === 'loading' && typeof c.placeKey === 'string' && c.placeKey.startsWith('reload-'),
    );
    if (placeholderIdx >= 0) {
      next.splice(placeholderIdx, 1);
      if (placeholderIdx <= insertAt) insertAt = Math.max(0, insertAt - 1);
    }
    const firstNonLoadingIdx = next.findIndex((c) => c.type !== 'loading');
    const loadingInsertIdx = firstNonLoadingIdx < 0 ? next.length : firstNonLoadingIdx;
    const newLoading: LoadingCard = {
      id: `loading-r${rotationNumber}-${lKey}`,
      type: 'loading',
      round: rotationNumber,
      locationName: legRun.loading_name,
      placeKey: lKey,
      legRunIds: [legRunId],
      laneId: legRun.shipment_order_lane_id ?? null,
    };
    next.splice(loadingInsertIdx, 0, newLoading);
    if (loadingInsertIdx <= insertAt) insertAt += 1;
  }

  // 2) Unloading
  // 既に同 legRun を含む delivery カードが居る場合は no-op (重複作成を防ぐ)。
  // 例: 元 row が FR template で同名候補に含まれていた + ドロップで実 delivery 化、というケースで
  //     placeKey が異なる別カードになり同 legRun が 2 枚並ぶ → React duplicate key 警告。
  const alreadyPlacedIdx = next.findIndex(
    (c) => c.type === 'delivery'
      && c.status !== 'no-delivery'
      && c.status !== 'fr-template'
      && (c.legRunIds ?? []).includes(legRunId),
  );
  if (alreadyPlacedIdx >= 0) {
    return next;
  }

  const uKey = unloadingPlaceKey(legRun);
  const unloadingIdx = next.findIndex(
    (c) => c.type === 'delivery' && c.status !== 'no-delivery' && c.placeKey === uKey,
  );
  if (unloadingIdx >= 0) {
    const existing = next[unloadingIdx] as DeliveryCard;
    if (existing.status === 'fr-template') {
      // FR 由来の仮カード (legRunIds = FR 候補集合) を、ドロップした 1 件の 'template' 状態に置き換える。
      // 単純 merge だと legRunIds に既に含まれていて no-op になり、status='fr-template' のまま
      // 残って runAutoDispatch の currentLocation 集計から除外される (= auto-dispatch が発火しない)。
      next[unloadingIdx] = {
        ...existing,
        cases: legRun.item_count ?? 0,
        todayCases: legRun.item_count ?? 0,
        weight: legRun.item_total_weight_kg ?? null,
        volume: legRun.item_total_volume_m3 ?? null,
        status: 'template',
        legRunIds: [legRunId],
        origin: legRun.loading_name,
        destination: legRun.unloading_name,
        shippers: legRun.shipper_name ? [legRun.shipper_name] : undefined,
        laneId: legRun.shipment_order_lane_id ?? null,
        frStopType: undefined,
      };
    } else if (!(existing.legRunIds ?? []).includes(legRunId)) {
      const addCases = legRun.item_count ?? 0;
      const addWeight = legRun.item_total_weight_kg ?? 0;
      const addVolume = legRun.item_total_volume_m3 ?? 0;
      next[unloadingIdx] = {
        ...existing,
        cases: (existing.cases ?? 0) + addCases,
        todayCases: (existing.todayCases ?? 0) + addCases,
        weight: (existing.weight ?? 0) + addWeight,
        volume: (existing.volume ?? 0) + addVolume,
        legRunIds: [...(existing.legRunIds ?? []), legRunId],
      };
    }
  } else {
    const newDelivery: DeliveryCard = {
      // truckId / rotationNumber を含めることで複数 row 間で legRun id が重複しても
      // React の key 衝突を起こさない (D&D 移動中 / FR template 経由を想定)
      id: truckId != null
        ? `lr-${truckId}-${rotationNumber}-${legRunId}`
        : `lr-${legRunId}`,
      type: 'delivery',
      storeName: legRun.unloading_name || '—',
      storeAddress: legRun.unloading_address,
      geo: legRun.unloading_lat != null && legRun.unloading_lng != null
        ? { lat: legRun.unloading_lat, lng: legRun.unloading_lng }
        : undefined,
      cases: legRun.item_count ?? 0,
      todayCases: legRun.item_count ?? 0,
      weight: legRun.item_total_weight_kg ?? null,
      volume: legRun.item_total_volume_m3 ?? null,
      // ドラッグ&ドロップで置いた直後は未反映（template）状態。
      // auto-dispatch (timelinePlanning) が完了してキャッシュが更新されると 'matched' に置き換わる。
      status: 'template',
      isActive: true,
      placeKey: uKey,
      legRunIds: [legRunId],
      origin: legRun.loading_name,
      destination: legRun.unloading_name,
      shippers: legRun.shipper_name ? [legRun.shipper_name] : undefined,
      laneId: legRun.shipment_order_lane_id ?? null,
    };
    const firstNonLoadingIdx = next.findIndex((c) => c.type !== 'loading');
    const minUnloadingIdx = firstNonLoadingIdx < 0 ? next.length : firstNonLoadingIdx;
    const finalInsertAt = Math.max(insertAt, minUnloadingIdx);
    next.splice(Math.min(finalInsertAt, next.length), 0, newDelivery);
  }

  return next;
}

type Props = {
  startOn: string;
  endOn: string;
  fixedRouteId: number | null;
  fixedRoutes: ConvenienceDispatchFixedRouteEntity[];
  isLoadingFixedRoutes: boolean;
  isApplying: boolean;
  deliveriesQuery: UseInfiniteQueryResult<DeliveriesResponse>;
  legRunsQuery: UseInfiniteQueryResult<ShipmentOrderLegRunListResponse>;
  fixedRouteDetailQuery: UseQueryResult<ConvenienceDispatchFixedRouteDetailEntity>;
  allDeliveriesQuery: UseQueryResult<PlanningsDeliveryWithLegRunEntity[]>;
  trucks: PlanningsTruckEntity[];
  drivers: PlanningsDriverEntity[];
  onDateRangeChange: (startOn: string, endOn: string) => void;
  onFixedRouteSelect: (fixedRouteId: number) => void;
  onFixedRouteCreated: (newFixedRouteId: number) => void;
  onFixedRouteCleared: () => void;
  /** toolbar の固定ルート選択経由 (= 呼び出し)。apply のみ、timeline 自動配車は走らせない。
   *  overrideId が指定されれば state の fixedRouteId より優先する（state 更新を待たずに id を指定できる）。 */
  onApplyFixedRoute: (overrideId?: number) => void;
  /** 未割当パネルの「固定ルートに対応づけ」ボタン経由。apply 後に timeline 自動配車を自動発火する。 */
  onAlignToFixedRoute: (overrideId?: number) => void;
  /** Backdrop 文言切り替え用: 'call' = 呼び出し / 'align' = 対応づけ */
  applyMode: 'call' | 'align';
  onNavigate: (path: string) => void;
};

const ConvenienceDispatchesPresenter: FC<Props> = memo(
  ({
    startOn,
    endOn,
    fixedRouteId,
    fixedRoutes,
    isLoadingFixedRoutes,
    isApplying,
    deliveriesQuery,
    legRunsQuery,
    fixedRouteDetailQuery,
    allDeliveriesQuery,
    trucks,
    drivers,
    onDateRangeChange,
    onFixedRouteSelect,
    onFixedRouteCreated,
    onFixedRouteCleared,
    onApplyFixedRoute,
    onAlignToFixedRoute,
    applyMode,
    onNavigate,
  }) => {
    // 表示項目 / 表示列数はリロード後も維持するため localStorage に永続化する。
    // 不正な保存値はバリデータで弾いて初期値に戻す。
    const [displayMetrics, setDisplayMetrics] = usePersistedJSON<DisplayMetric[]>(
      'convenienceDispatch.displayMetrics',
      ['quantity'],
      isDisplayMetricArray,
    );
    const displayMetricSet = useMemo(() => new Set(displayMetrics), [displayMetrics]);

    const [visibleColumns, setVisibleColumns] = usePersistedJSON<number>(
      'convenienceDispatch.visibleColumns',
      10,
      isPositiveInteger,
    );
    const [isRightPanelOpen, setIsRightPanelOpen] = useState(true);

    const deliveries = useMemo(
      () => deliveriesQuery.data?.pages.flatMap((page) => page.data) ?? [],
      [deliveriesQuery.data],
    );

    const legRuns = useMemo(
      () => legRunsQuery.data?.pages.flatMap((page) => page.data) ?? [],
      [legRunsQuery.data],
    );

    // 全ページを自動取得する（V3Timeline と同じパターン）
    useEffect(() => {
      if (!legRunsQuery.isLoading && !legRunsQuery.isFetching && legRunsQuery.hasNextPage) {
        void legRunsQuery.fetchNextPage();
      }
    }, [legRunsQuery]);
    useEffect(() => {
      if (!deliveriesQuery.isLoading && !deliveriesQuery.isFetching && deliveriesQuery.hasNextPage) {
        void deliveriesQuery.fetchNextPage();
      }
    }, [deliveriesQuery]);

    void allDeliveriesQuery;

    // `/plannings/.../deliveries` の `shipmentOrderLegRuns[]` は compact=true でシリアライズ
    // されており、数量 / 備考 / 作業秒 / 指定時間 などが抜けている。
    // 一方 `/api/v5/shipment_order_leg_runs` は非 compact なので、id で突き合わせて補完する。
    const legRunMaterialsMap = useMemo(() => {
      const m = new Map<number, ShipmentOrderLegRunForPlanEntity>();
      for (const lr of legRuns) {
        if (lr.id != null) m.set(lr.id, lr);
      }
      return m;
    }, [legRuns]);

    // deliveries のコピーを作って legRun を補完してから buildVehicleRows に渡す
    const enrichedDeliveries = useMemo(() => {
      if (legRunMaterialsMap.size === 0) return deliveries;
      return deliveries.map((d) => ({
        ...d,
        shipmentOrderLegRuns: d.shipmentOrderLegRuns.map((lr) => {
          if (lr.id == null) return lr;
          const rich = legRunMaterialsMap.get(lr.id);
          if (!rich) return lr;
          // 非 compact 側 (rich) に存在するフィールドで補完する。
          // delivery 由来の compact 値 (lr) を base にし、rich の値で上書き。
          return { ...lr, ...rich };
        }),
      }));
    }, [deliveries, legRunMaterialsMap]);

    // 未割当 legRun（assigned=false）を buildVehicleRows の差分判定へ渡す。
    // 固定ルートにある店舗が未割当にも存在する場合は「本日なし」を出さないため。
    const unassignedLegRunsForVM = useMemo(
      () => legRuns.filter((lr) => !lr.assigned),
      [legRuns],
    );

    // VehicleRowVM を buildVehicleRows で組み立てる（既存のデータパイプラインは維持）
    const sourceRowsRaw = useMemo(
      () => buildVehicleRows(
        enrichedDeliveries,
        fixedRouteId != null ? fixedRouteDetailQuery.data : null,
        unassignedLegRunsForVM,
        trucks,
        drivers,
      ),
      [enrichedDeliveries, fixedRouteId, fixedRouteDetailQuery.data, unassignedLegRunsForVM, trucks, drivers],
    );

    // 「回転追加」によりローカルで追加された仮想行: truckId → rotationNumber[]
    const [extraRotations, setExtraRotations] = useState<Map<number, number[]>>(new Map());

    // 案件のない placeholder row (FR にのみ存在する rotation で当日 operation 未生成) を
    // ユーザーが × ボタンで「今は使わない」と指示した場合のローカル隠蔽: truckId → rotationNumber[]
    // 「呼び出し」「対応づけ」再実行や日付変更でリセットされ復活する。
    const [hiddenPlaceholderRotations, setHiddenPlaceholderRotations] = useState<Map<number, Set<number>>>(new Map());

    // 日付変更 effect で running キャッシュを invalidate するため、ここで queryClient を取得しておく。
    const queryClient = useQueryClient();

    // 日付変更時に extraRotations / hiddenPlaceholderRotations をリセット
    useEffect(() => {
      setExtraRotations(new Map());
      setHiddenPlaceholderRotations(new Map());
      // 自動配車の running 状態も日付変更で破棄 (古い planning ID を別日付で使わないように)
      setAsyncPlanningResponse(null);
      setRunningPlanningId(null);
      activePollingSessionRef.current = null;
      // 日付変更で元日付ジョブの polling は破棄されるため、その後バックエンドで元日付のジョブが
      // 完了/継続しても元日付の running キャッシュを更新できなくなる。ref を破棄する前に元日付の
      // キャッシュを invalidate しておき、古い「実行中」が固定されたまま残らないようにする。
      const prevRange = runningPlanningRangeRef.current;
      runningPlanningRangeRef.current = null;
      if (prevRange) {
        void invalidateRunningPlanningsCache(queryClient, prevRange.startOn, prevRange.endOn);
      }
    }, [startOn, endOn, queryClient]);

    // 固定ルート変更時にも hiddenPlaceholderRotations をリセット (別 FR の rotation 構成に
    // 引き継ぐと意味が崩れるため)。
    useEffect(() => {
      setHiddenPlaceholderRotations(new Map());
    }, [fixedRouteId]);

    // サーバで既に存在する回転を extraRotations から落とす（重複防止）
    useEffect(() => {
      setExtraRotations((prev) => {
        if (prev.size === 0) return prev;
        const serverRotationsByTruck = new Map<number, Set<number>>();
        for (const r of sourceRowsRaw) {
          const set = serverRotationsByTruck.get(r.truckId);
          if (set) set.add(r.rotationNumber);
          else serverRotationsByTruck.set(r.truckId, new Set([r.rotationNumber]));
        }
        const next = new Map<number, number[]>();
        let changed = false;
        for (const [truckId, rns] of prev) {
          const serverSet = serverRotationsByTruck.get(truckId);
          const filtered = rns.filter((rn) => !(serverSet && serverSet.has(rn)));
          if (filtered.length !== rns.length) changed = true;
          if (filtered.length > 0) next.set(truckId, filtered);
        }
        return changed ? next : prev;
      });
    }, [sourceRowsRaw]);

    // sourceRowsRaw に extraRotations をマージし placeholder 行として追加
    // 回転追加で作られた placeholder 行には「再積み込み拠点」の loading カードを初期配置する
    const sourceRows = useMemo<VehicleRowVM[]>(() => {
      if (extraRotations.size === 0) return sourceRowsRaw;
      let placeholderSeed = -2000;
      const extras: VehicleRowVM[] = [];
      for (const [truckId, rns] of extraRotations) {
        for (const rn of rns) {
          extras.push({
            deliveryId: placeholderSeed--,
            truckId,
            rotationNumber: rn,
            totalRotations: 0,
            driverColor: '#9e9e9e',
            totalItemCount: 0,
            cards: [{
              kind: 'loading',
              placeKey: `reload-${truckId}-${rn}`,
              placeName: '再積み込み拠点',
              timeLabel: null,
              itemCount: 0,
              operations: [],
              legRuns: [],
              isRouteDeviation: false,
              isAutoAssigned: false,
            }],
            isPlaceholderRow: true,
            isExtraRotation: true,
          });
        }
      }
      const merged: VehicleRowVM[] = [...sourceRowsRaw, ...extras];
      const rotCount = new Map<number, number>();
      merged.forEach((r) => rotCount.set(r.truckId, (rotCount.get(r.truckId) ?? 0) + 1));
      return merged.map((r) => ({ ...r, totalRotations: rotCount.get(r.truckId) ?? 1 }));
    }, [sourceRowsRaw, extraRotations]);

    // truckId → VehicleInfoExtra マップ構築
    const truckCapacityMap = useMemo<Map<number, number | null>>(() => {
      const map = new Map<number, number | null>();
      for (const d of deliveries) {
        if (!map.has(d.truckId)) {
          map.set(d.truckId, d.maximumLoadingCapacityQuantity);
        }
      }
      return map;
    }, [deliveries]);

    // truckId / driverId → マスタ実体
    const truckMasterMap = useMemo(() => {
      const m = new Map<number, PlanningsTruckEntity>();
      for (const t of trucks) m.set(t.id, t);
      return m;
    }, [trucks]);
    const driverMasterMap = useMemo(() => {
      const m = new Map<number, PlanningsDriverEntity>();
      for (const d of drivers) m.set(d.id, d);
      return m;
    }, [drivers]);
    // truckId → その車両に割り当てられている代表的な driverId (deliveries 側から抽出)
    const truckDriverMap = useMemo(() => {
      const m = new Map<number, number>();
      for (const d of deliveries) {
        if (!m.has(d.truckId)) m.set(d.truckId, d.driverId);
      }
      return m;
    }, [deliveries]);

    const vehicleExtraMap = useMemo<Map<number, VehicleInfoExtra>>(() => {
      const detail = fixedRouteDetailQuery.data;
      const map = new Map<number, VehicleInfoExtra>();

      // truck master からの fallback
      // PlanningsTruckEntity は
      //   - 重量: maximumLoadingCapacityWeightForCalculation (g) のみ
      //   - 体積: loadingPlatformVolumeMm3 (mm³) のみ
      // を持つため、単位変換して採用する
      // V3Plans / V3Timeline と同様に temporary truck / driver は「指定なし」表示
      // truckId → 紐づく shift_truck.id (= delivery.id) を引くマップ。差し替え API のターゲット。
      const shiftTruckByTruckId = new Map<number, number>();
      // 当日運用車番 (shift_trucks.truck_number) を truckId 単位で引けるように
      const shiftTruckNumberByTruckId = new Map<number, string | null>();
      // 当日勤務時間 (shift_truck.start_at / end_at) を truckId 単位で引けるように
      const shiftTimeByTruckId = new Map<number, { start: string | null; end: string | null }>();
      for (const d of deliveries) {
        if (!shiftTruckByTruckId.has(d.truckId)) {
          shiftTruckByTruckId.set(d.truckId, d.id);
          shiftTruckNumberByTruckId.set(d.truckId, d.truckNumber ?? null);
          shiftTimeByTruckId.set(d.truckId, {
            start: formatHourMinute(d.startAt),
            end: formatHourMinute(d.endAt),
          });
        }
      }

      const makeExtra = (truckId: number, capacityQuantity: number | null): VehicleInfoExtra => {
        const truck = truckMasterMap.get(truckId);
        const driverId = truckDriverMap.get(truckId) ?? null;
        const driver = driverId != null ? driverMasterMap.get(driverId) : undefined;
        // truck.license_plate_value = "車両名" として表示 (例: "5号車" / "品川あ100-110")
        const truckName = truck?.temporary ? '指定なし' : (truck?.licensePlateValue || null);
        // 車番の優先順位: shift_truck.truck_number → (adapter で) leg_run.truck_number → truck.truck_number
        const shiftTruckNumber = shiftTruckNumberByTruckId.get(truckId) ?? null;
        const masterTruckNumber = truck?.temporary ? null : (truck?.truckNumber ?? null);
        const driverName = driver?.temporary ? '指定なし' : (driver?.name ?? null);
        const shiftTime = shiftTimeByTruckId.get(truckId);
        const maxG = truck?.maximumLoadingCapacityWeightForCalculation ?? 0;
        const maxMm3 = truck?.loadingPlatformVolumeMm3 ?? 0;
        return {
          partnerCompanyName: truck?.temporary ? null : truck?.partnerCompanyName ?? null,
          truckName,
          shiftTruckNumber,
          masterTruckNumber,
          truckLabel: truckName,
          defaultDriverName: driverName,
          shiftStart: shiftTime?.start ?? null,
          shiftEnd: shiftTime?.end ?? null,
          driverId,
          shiftTruckId: shiftTruckByTruckId.get(truckId) ?? null,
          capacityCases: null,
          capacityQuantity,
          capacityKg: maxG > 0 ? maxG / 1000 : null,
          capacityM3: maxMm3 > 0 ? maxMm3 / 1e9 : null,
        };
      };

      if (detail) {
        for (const v of detail.vehicles) {
          if (v.truckId != null) {
            const capacityQuantity = truckCapacityMap.has(v.truckId)
              ? truckCapacityMap.get(v.truckId) ?? null
              : (v.capacityQuantity ?? null);
            const fallback = makeExtra(v.truckId, capacityQuantity);
            map.set(v.truckId, {
              partnerCompanyName: v.partnerCompanyName ?? fallback.partnerCompanyName,
              truckName: fallback.truckName,
              shiftTruckNumber: fallback.shiftTruckNumber,
              masterTruckNumber: fallback.masterTruckNumber,
              truckLabel: v.truckLabel ?? fallback.truckLabel,
              defaultDriverName: v.defaultDriverName ?? fallback.defaultDriverName,
              shiftStart: fallback.shiftStart,
              shiftEnd: fallback.shiftEnd,
              driverId: fallback.driverId,
              shiftTruckId: fallback.shiftTruckId,
              capacityCases: v.capacityCases,
              capacityQuantity,
              capacityKg: fallback.capacityKg,
              capacityM3: fallback.capacityM3,
            });
          }
        }
      }
      // fixed route 未選択車両は truck master のみから埋める
      for (const [truckId, capacityQuantity] of truckCapacityMap) {
        if (!map.has(truckId)) {
          map.set(truckId, makeExtra(truckId, capacityQuantity ?? null));
        }
      }
      return map;
    }, [fixedRouteDetailQuery.data, truckCapacityMap, truckMasterMap, driverMasterMap, truckDriverMap]);

    // VehicleRowVM → VehicleRowGroup (= Vehicle + RotationRow[]) に変換（サーバ由来）
    const serverRowGroups = useMemo(
      () => buildVehicleRowGroups(sourceRows, vehicleExtraMap),
      [sourceRows, vehicleExtraMap],
    );
    // 未割当リストは assigned=false (= delivery に紐付いていない) legRun のみを表示
    const serverUnassigned = useMemo<UnassignedItem[]>(
      () => legRuns.filter((lr) => !lr.assigned).map((lr, idx) => toUnassignedItem(lr, idx)),
      [legRuns],
    );

    // D&D 用のローカル上書き状態
    // 各 row の cards 配列、未割当 item 配列をローカルで管理する。
    // サーバデータが変わる度に再初期化される。
    const [overrideRowCards, setOverrideRowCards] = useState<Map<string, DispatchCard[]>>(new Map());
    const [overrideUnassigned, setOverrideUnassigned] = useState<UnassignedItem[] | null>(null);
    // 自動配車中に追加操作が入ったかどうか。古い job の完了/refetch で
    // 操作途中の override を消さないため、state ではなく同期的に読める ref で持つ。
    const hasQueuedOverrideChangeRef = useRef(false);
    const overrideStateRef = useRef({ overrideRowCount: 0, hasOverrideUnassigned: false });
    useEffect(() => {
      overrideStateRef.current = {
        overrideRowCount: overrideRowCards.size,
        hasOverrideUnassigned: overrideUnassigned !== null,
      };
    }, [overrideRowCards, overrideUnassigned]);

    // 楽観的 UI 更新用のローカル上書き (車両名 / 車番 / ドライバー名)。key = 元の truckId。
    // mutation 発火と同時に表示を切り替え、refetch 完了後にサーバ値に置き換える。
    // refetch 後にサーバが同じ値を返せば override を消しても見た目は変わらない (ちらつき無し)。
    const [vehicleOverrides, setVehicleOverrides] = useState<Map<number, VehicleDisplayOverride>>(new Map());
    const setVehicleOverride = useCallback((truckId: number, override: VehicleDisplayOverride) => {
      setVehicleOverrides((prev) => {
        const next = new Map(prev);
        next.set(truckId, { ...next.get(truckId), ...override });
        return next;
      });
    }, []);
    const clearVehicleOverride = useCallback((truckId: number) => {
      setVehicleOverrides((prev) => {
        if (!prev.has(truckId)) return prev;
        const next = new Map(prev);
        next.delete(truckId);
        return next;
      });
    }, []);

    // サーバデータ変化時のみローカル上書きをリセット。
    // extraRotations（回転追加）の変化では serverRowGroups も再計算されるが、
    // それでリセットしてしまうと D&D で置いた案件が消える。
    // サーバ純粋由来である sourceRowsRaw / serverUnassigned を依存にする。
    useEffect(() => {
      const overrideState = overrideStateRef.current;
      if (!shouldResetOverrideAfterServerRefresh({
        overrideRowCount: overrideState.overrideRowCount,
        hasOverrideUnassigned: overrideState.hasOverrideUnassigned,
        hasQueuedOverrideChange: hasQueuedOverrideChangeRef.current,
      })) return;
      setOverrideRowCards(new Map());
      setOverrideUnassigned(null);
    }, [sourceRowsRaw, serverUnassigned]);

    // 実表示用の rowGroups / unassigned
    const rowGroups = useMemo<VehicleRowGroup[]>(() => {
      if (overrideRowCards.size === 0) return serverRowGroups;

      // override で配置された legRunId を収集
      // → 他の行の仮カード (fr-template) でこの legRun を含むものは非表示にする
      // （固定ルート外や別車両に落とされたら、元の固定ルート位置の仮カードは消える）
      const placedLegRunIds = new Set<number>();
      for (const cards of overrideRowCards.values()) {
        for (const c of cards) {
          if (c.type !== 'delivery') continue;
          (c.legRunIds ?? []).forEach((id) => placedLegRunIds.add(id));
        }
      }

      const hidePlacedFrTemplate = (cards: DispatchCard[]): DispatchCard[] => {
        if (placedLegRunIds.size === 0) return cards;
        return cards.filter((c) => {
          if (c.type !== 'delivery' || c.status !== 'fr-template') return true;
          // 積地 (frStopType='loading') は複数 truck が同じ積地で legRun を候補に持つことがあり、
          // 片方の row に legRun が確定しても他 row の積地候補は残るべき。フィルタ対象は降地のみ。
          if (c.frStopType === 'loading') return true;
          return !(c.legRunIds ?? []).some((id) => placedLegRunIds.has(id));
        });
      };

      return serverRowGroups.map((group) => {
        const newRows = group.rows.map((row) => {
          const override = overrideRowCards.get(row.rowId);
          if (override) {
            return {
              ...row,
              cards: override,
              deliveryCards: override.filter(
                (c): c is DeliveryCard => c.type === 'delivery' && c.status !== 'no-delivery' && c.status !== 'fr-template',
              ),
            };
          }
          const filtered = hidePlacedFrTemplate(row.cards);
          if (filtered === row.cards) return row;
          return {
            ...row,
            cards: filtered,
            deliveryCards: filtered.filter(
              (c): c is DeliveryCard => c.type === 'delivery' && c.status !== 'no-delivery' && c.status !== 'fr-template',
            ),
          };
        });
        const allCards = newRows.flatMap((r) => r.cards);
        return { ...group, rows: newRows, vehicle: { ...group.vehicle, cards: allCards } };
      });
    }, [serverRowGroups, overrideRowCards]);

    const unassigned = overrideUnassigned ?? serverUnassigned;

    // rowId ベースの選択状態
    const [selectedRowIds, setSelectedRowIds] = useState<Set<string>>(new Set());
    // 車両表示ステータス。リロード後も維持するため localStorage に永続化する。
    const [truckDisplayStatus, setTruckDisplayStatus] = usePersistedJSON<TruckDisplayStatusVo>(
      'convenienceDispatch.truckDisplayStatus',
      'すべて表示',
      isTruckDisplayStatus,
    );

    useEffect(() => {
      setSelectedRowIds(new Set());
    }, [startOn, endOn]);

    const onRowToggle = useCallback((rowId: string) => {
      setSelectedRowIds((prev) => {
        const next = new Set(prev);
        if (next.has(rowId)) next.delete(rowId);
        else next.add(rowId);
        return next;
      });
    }, []);

    // 表示するトラックグループ (V3Plans の PlanningsGroupEntity 仕組み)。
    // V3Plans Component.tsx と同様、先頭に id=0 「すべて表示する」を持つ合成エントリを prepend し、
    // index ベースで多選択する (TruckDisplayStatusMenu の規約)。
    const { data: planningGroupsData } = useQueryPlanningGroups();
    const allDisplayedTruckIds = useMemo(
      () => rowGroups
        .map((g) => Number(g.vehicle.id.replace('truck-', '')))
        .filter((id) => Number.isFinite(id)),
      [rowGroups],
    );
    const truckGroupOptions = useMemo(() => {
      const groups = planningGroupsData ?? [];
      // 表示対象トラックを 1 台でも持っているグループに絞る (V3Plans/Component.tsx 同等)
      const displayed = new Set(allDisplayedTruckIds);
      return groups.filter((g) => g.truckIds.some((id) => displayed.has(id)));
    }, [planningGroupsData, allDisplayedTruckIds]);
    const truckGroupEntitiesWithAll = useMemo<PlanningsGroupEntity[]>(() => {
      if (truckGroupOptions.length === 0) return [];
      return [
        { id: 0, name: 'すべて表示する', truckIds: allDisplayedTruckIds },
        ...truckGroupOptions,
      ];
    }, [truckGroupOptions, allDisplayedTruckIds]);

    // 選択中の group index (0 = すべて表示する)。永続化対象。
    const [selectedTruckGroupIndices, setSelectedTruckGroupIndices] = usePersistedJSON<number[]>(
      'convenienceDispatch.selectedTruckGroupIndices',
      [0],
      isNumberArray,
    );
    // 永続化値が現在の groupEntities と齟齬を起こした場合の補正:
    //   index が範囲外なら除去。空集合になったら id=0 (= すべて) に戻す。
    useEffect(() => {
      if (truckGroupEntitiesWithAll.length === 0) return;
      const valid = selectedTruckGroupIndices.filter((i) => i >= 0 && i < truckGroupEntitiesWithAll.length);
      if (valid.length === selectedTruckGroupIndices.length) return;
      setSelectedTruckGroupIndices(valid.length === 0 ? [0] : valid);
    }, [truckGroupEntitiesWithAll, selectedTruckGroupIndices, setSelectedTruckGroupIndices]);

    // 選択された index 群から「許可された truckId 集合」を作る。
    // null は「フィルタ無し (= すべて表示)」を意味する。
    const allowedTruckIds = useMemo<Set<number> | null>(() => {
      if (truckGroupEntitiesWithAll.length === 0) return null; // グループ未定義
      if (selectedTruckGroupIndices.includes(0)) return null;  // 「すべて表示する」
      const ids = new Set<number>();
      for (const i of selectedTruckGroupIndices) {
        const g = truckGroupEntitiesWithAll[i];
        if (g) g.truckIds.forEach((id) => ids.add(id));
      }
      return ids;
    }, [truckGroupEntitiesWithAll, selectedTruckGroupIndices]);

    // 車両表示順 (PlanningTrucksPresenter と同じ仕組み)。ドラッグで即座に並びを変える楽観的更新を
    // するために、サーバ確定前の order を local state で保持する。range が変わったらリセット。
    const canSortTruckDisplayOrder = startOn === endOn;
    const truckDisplayOrderMutation = useMutationPlanningTruckDisplayOrders(startOn, endOn);
    const planningRangeKey = `${startOn}:${endOn}`;
    const [sortedTruckIdsState, setSortedTruckIdsState] = useState<{
      rangeKey: string;
      ids: number[];
    }>({ rangeKey: '', ids: [] });
    const sortedTruckIds = useMemo(
      () => (sortedTruckIdsState.rangeKey === planningRangeKey ? sortedTruckIdsState.ids : []),
      [planningRangeKey, sortedTruckIdsState],
    );

    // フィルタ: 車両表示ステータス (V3Plans 互換) + トラックグループ。両方適用 (AND)。
    //   配車済み車両のみ表示 : truck の rows に operation を持つ row が 1 つでもあれば表示
    //   未配車の車両のみ表示  : 上記の逆 (= 配車されていない truck)
    //   選択中のみ表示する    : 選択中の row のみ表示
    const filteredRowGroups = useMemo(() => {
      let result = rowGroups;
      // 楽観的更新の override を Vehicle 表示フィールドにマージ (truckName / plateNumber / driverName)
      if (vehicleOverrides.size > 0) {
        result = result.map((g) => {
          const ov = g.vehicle.truckId != null ? vehicleOverrides.get(g.vehicle.truckId) : undefined;
          return ov ? { ...g, vehicle: { ...g.vehicle, ...ov } } : g;
        });
      }
      // ユーザーが × ボタンで非表示にした placeholder rotation を除外
      // (実配送 row は同じ rotationNumber でも除外しない: adapter.ts 参照)
      result = filterHiddenPlaceholderRotations(result, hiddenPlaceholderRotations);
      if (allowedTruckIds) {
        result = result.filter((g) => {
          const truckIdNum = Number(g.vehicle.id.replace('truck-', ''));
          return Number.isFinite(truckIdNum) && allowedTruckIds.has(truckIdNum);
        });
      }
      if (truckDisplayStatus === '配車済み車両のみ表示') {
        result = result.filter((g) => g.rows.some((r) => r.source.cards.some((c) => c.operations.length > 0)));
      } else if (truckDisplayStatus === '未配車の車両のみ表示') {
        result = result.filter((g) => g.rows.every((r) => r.source.cards.every((c) => c.operations.length === 0)));
      } else if (truckDisplayStatus === '選択中のみ表示する' && selectedRowIds.size > 0) {
        result = result
          .map((g) => ({ ...g, rows: g.rows.filter((r) => selectedRowIds.has(r.rowId)) }))
          .filter((g) => g.rows.length > 0);
      }
      // 楽観的更新の並び順を適用 (sortedTruckIds は drag 直後にセットされる)。
      // サーバ完了で query invalidate → rowGroups 再構築までの間、ドラッグ後の表示順を保持する。
      if (sortedTruckIds.length > 0) {
        const orderIndex = new Map(sortedTruckIds.map((id, idx) => [id, idx]));
        const truckIdOfGroup = (g: VehicleRowGroup): number | null => {
          const n = Number(g.vehicle.id.replace('truck-', ''));
          return Number.isFinite(n) ? n : null;
        };
        result = [...result].sort((a, b) => {
          const aId = truckIdOfGroup(a);
          const bId = truckIdOfGroup(b);
          const aIdx = aId != null ? orderIndex.get(aId) ?? -1 : -1;
          const bIdx = bId != null ? orderIndex.get(bId) ?? -1 : -1;
          if (aIdx === -1 && bIdx === -1) return 0;
          if (aIdx === -1) return 1;
          if (bIdx === -1) return -1;
          return aIdx - bIdx;
        });
      }
      return result;
    }, [rowGroups, truckDisplayStatus, selectedRowIds, allowedTruckIds, vehicleOverrides, hiddenPlaceholderRotations, sortedTruckIds]);

    const allRowIds = useMemo(
      () => rowGroups.flatMap((g) => g.rows
        // FR 合成行 (isPlaceholderRow=true && !isExtraRotation) は実在する delivery が無いので
        // 「全選択」対象から除外。ローカル追加した 2 回転目以降 (isExtraRotation=true) は同 truck の
        // 代表 delivery にぶら下げて配車できるので含める。
        .filter((r) => !r.source.isPlaceholderRow || r.source.isExtraRotation === true)
        .map((r) => r.rowId)),
      [rowGroups],
    );
    const allSelected = allRowIds.length > 0 && allRowIds.every((id) => selectedRowIds.has(id));

    const onSelectAll = useCallback(() => {
      setSelectedRowIds(new Set(allRowIds));
    }, [allRowIds]);
    const onDeselectAll = useCallback(() => setSelectedRowIds(new Set()), []);

    // 既存車両/ドライバー（AddVehicleDialog 用）
    const existingTruckIds = useMemo(() => new Set(sourceRows.filter((r) => !r.isPlaceholderRow).map((r) => r.truckId)), [sourceRows]);
    const existingDriverIds = useMemo(() => new Set(deliveries.map((d) => d.driverId)), [deliveries]);

    const [addVehicleDialogOpen, setAddVehicleDialogOpen] = useState(false);
    const [fixedRouteSelectorOpen, setFixedRouteSelectorOpen] = useState(false);

    // 汎用確認ダイアログ
    const [confirmDialog, setConfirmDialog] = useState<{
      title: string;
      description: string;
      onConfirm: () => void | Promise<void>;
    } | null>(null);

    const { enqueueSnackbar } = useSnackbar();
    const { operationBulkResetting, timelinePlanning, normalPlanning, transferPlanning } = useMutationPlanningV5();
    const { restoreSplitLegRun } = useMutationShipmentOrderLegRun();

    const handleTruckOrderChange = useCallback((visibleNextIds: number[]) => {
      if (!canSortTruckDisplayOrder) return;
      // visibleNextIds はフィルタ範囲内 (filteredRowGroups) のみの並び順。
      // API は受け取った truck_ids だけで version を再作成するため、フィルタ外・delivery なしの
      // 車両も含めた全車両 (trucks) を base にマージして送る。フィルタ範囲外の車両は base の
      // 元の位置を維持し、フィルタ範囲内の slot だけを visibleNextIds の順で埋める。
      // 例) base = [1, 2, 3, 4, 5], visibleNext = [5, 1, 3] (filter で 2,4 は非表示)
      //   → merged = [5, 2, 1, 4, 3]
      //
      // base 選定:
      //  - 既に DnD 後の楽観的 state (sortedTruckIds) があればそれを優先 (連続 drag 対応)
      //  - そうでなければ trucks マスタを API と同じ規則 (displayOrder bucket → sortOrder) で
      //    並べた順を使う (= rowGroups は delivery 由来で未配車車両が含まれないので使えない)
      const baseTruckIds = sortedTruckIds.length > 0
        ? sortedTruckIds
        : trucks
          .slice()
          .sort(compareTrucksByDisplayOrder)
          .map((t) => t.id);

      const visibleSet = new Set(visibleNextIds);
      let pointer = 0;
      const mergedIds = baseTruckIds.map((id) => {
        if (visibleSet.has(id) && pointer < visibleNextIds.length) {
          const next = visibleNextIds[pointer];
          pointer += 1;
          return next;
        }
        return id;
      });
      // 念のため: base にない visibleNextIds (= 新規 truck など) は末尾に append
      while (pointer < visibleNextIds.length) {
        mergedIds.push(visibleNextIds[pointer]);
        pointer += 1;
      }

      // 楽観的更新: ドラッグ直後に並びを反映する
      setSortedTruckIdsState({ rangeKey: planningRangeKey, ids: mergedIds });
      truckDisplayOrderMutation.mutate(mergedIds, {
        onError: () => {
          // ロールバック: rangeKey をクリアしてサーバ真値からの再構築を促す
          setSortedTruckIdsState({ rangeKey: '', ids: [] });
          enqueueSnackbar('車両表示順の保存に失敗しました', { variant: 'error' });
        },
      });
    }, [canSortTruckDisplayOrder, enqueueSnackbar, planningRangeKey, sortedTruckIds, trucks, truckDisplayOrderMutation]);

    // 自動配車: mutation 実行中または polling 中フラグを一元管理
    const [asyncPlanningResponse, setAsyncPlanningResponse] = useState<AsyncPlanningResponseEntity | null>(null);
    const [latestAlgorithmRequestVersion, setLatestAlgorithmRequestVersion] = useState(0);

    // 自動配車キャンセル用: 実行中の Planning ID を保持して DELETE /api/v3/plannings/running に渡す
    const [runningPlanningId, setRunningPlanningId] = useState<number | null>(null);
    const planningRunningsQuery = useQueryPlanningsRunnings(startOn, endOn);
    const planningRunningsRefetchRef = useRef(planningRunningsQuery.refetch);
    useEffect(() => {
      planningRunningsRefetchRef.current = planningRunningsQuery.refetch;
    }, [planningRunningsQuery.refetch]);
    const cancelRunningPlanningMut = useMutationPlanningRunnings();
    const cancelRunningPlanningMutRef = useRef(cancelRunningPlanningMut);
    useEffect(() => {
      cancelRunningPlanningMutRef.current = cancelRunningPlanningMut;
    }, [cancelRunningPlanningMut]);

    // polling 完了 handler が現在処理中の session_id。cancelRunningIfAny でクリアされる。
    // await 中にキャンセルされた場合、handler は session 不一致を検知して残りの処理を中断する。
    const activePollingSessionRef = useRef<string | null>(null);

    // polling 中ジョブの対象日付 (ジョブ開始時の startOn/endOn)。完了/キャンセル/エラー時の
    // running キャッシュ操作は、画面の現在表示日付ではなくこの「ジョブ開始時の日付」を使う。
    // 実行中に表示日付を切り替えても、別日付の running を誤って消さない / 元日付の running を
    // 取り残さないようにするため。
    const runningPlanningRangeRef = useRef<{ startOn: string; endOn: string } | null>(null);

    // 自動配車をキャンセルする。
    // - polling を即時停止 (setAsyncPlanningResponse(null))
    // - Planning ID が判っていれば backend にも cancel API を呼ぶ。
    //   ID 未取得の隙間 (mutation.success 直後) では「ローカルでは無視」だけ行い、
    //   backend job は走り切るが結果は捨てる (polling 停止 + queryKey 不一致で結果は反映されない)
    // - 操作 → 即時キャンセル の policy (Q2-A)
    const cancelRunningIfAny = useCallback(() => {
      if (asyncPlanningResponse === null && runningPlanningId === null) return;
      // キャッシュ操作と cancel API はジョブ開始時の日付を使う (実行中に表示日付を変えても誤らない)。
      // range はジョブ実行中であれば polling 開始時に必ずセット済み。
      const range = runningPlanningRangeRef.current;
      if (!range) {
        // 万一 range が無い場合でも、フロント状態はクリアして UI が「実行中」のまま固まらないようにする。
        // 対象日付が不明なためキャッシュ操作はスキップする。
        console.warn('[convenience-dispatch] runningPlanningRange is missing. Clearing local state anyway.');
        setRunningPlanningId(null);
        setAsyncPlanningResponse(null);
        activePollingSessionRef.current = null;
        return;
      }
      const id = runningPlanningId;
      runningPlanningRangeRef.current = null;
      setRunningPlanningId(null);
      setAsyncPlanningResponse(null);
      activePollingSessionRef.current = null;
      // 完了時と違いキャンセルはサーバ上でジョブが止まる保証がないため、楽観的に空へはしない。
      // - cancel API 成功時のみ共有 running キャッシュを空にする (= サーバ上も停止)。
      // - 失敗時 / ID 未取得の早期キャンセル (backend ジョブは走り切る) では、実態がまだ running の
      //   可能性があるため invalidate で再取得し、地図画面などのバナー・停止導線を誤って消さない。
      if (id !== null) {
        console.log('[convenience-dispatch] cancel running planning', id);
        cancelRunningPlanningMutRef.current.delete.mutate(
          { id, startOn: range.startOn, endOn: range.endOn },
          {
            onSuccess: () => { void clearRunningPlanningsCache(queryClient, range.startOn, range.endOn); },
            onError: (e) => {
              console.warn('[convenience-dispatch] cancel API failed (ignored)', e);
              void invalidateRunningPlanningsCache(queryClient, range.startOn, range.endOn);
            },
          },
        );
      } else {
        console.log('[convenience-dispatch] cancel without ID — backend job will be ignored');
        void invalidateRunningPlanningsCache(queryClient, range.startOn, range.endOn);
      }
    }, [asyncPlanningResponse, runningPlanningId, queryClient]);

    // 未割当案件があるときの「理由 / ヒント」表示用。polling 完了時に session_id を保持する。
    const [notAllocSessionId, setNotAllocSessionId] = useState<string | null>(null);
    const [suggestionDialogOpen, setSuggestionDialogOpen] = useState(false);
    const [showNotAllocSuggestionSnackbar, setShowNotAllocSuggestionSnackbar] = useState(false);
    const { data: notAllocSuggestions } = useQueryPlanningsSuggestions(notAllocSessionId ?? '');
    const { data: notAllocReasonsData } = useQueryPlanningsNotAllocReasons(notAllocSessionId ?? '');
    // order_id が leg_run.id / shipment_order.id どちらで返っても拾えるよう、両方を key にする
    const notAllocReasonsByOrderId = useMemo(() => {
      const m = new Map<number, PlanningsNotAllocReason[]>();
      const add = (key: number, reason: PlanningsNotAllocReason) => {
        const bucket = m.get(key) ?? [];
        bucket.push(reason);
        m.set(key, bucket);
      };
      (notAllocReasonsData ?? []).forEach((r) => {
        add(r.order_id, r);
        const lr = legRuns.find((x) => x.shipment_order_id === r.order_id);
        if (lr?.id != null && lr.id !== r.order_id) add(lr.id, r);
      });
      console.log('[convenience-dispatch] not_alloc_reasons', {
        sessionId: notAllocSessionId,
        rawCount: notAllocReasonsData?.length ?? 0,
        mapKeys: Array.from(m.keys()),
      });
      return m;
    }, [notAllocReasonsData, notAllocSessionId, legRuns]);
    // NotAllocReasonPresenter が要求する型は PlanningsDeliveryEntity[] だが
    // 使われるのは id / truckId / driverId のみ。キャストで渡す。
    const notAllocDeliveriesForReason = useMemo(
      () => deliveries as unknown as PlanningsDeliveryEntity[],
      [deliveries],
    );
    useEffect(() => {
      setShowNotAllocSuggestionSnackbar(
        (notAllocSuggestions?.length ?? 0) > 0,
      );
    }, [notAllocSuggestions]);
    const handleSuggestionDialogClose = useCallback((_e: MouseEvent<HTMLElement>) => {
      setSuggestionDialogOpen(false);
    }, []);

    // 配車のヒント (NotAllocSuggestion snackbar) を即時クリアする。
    // 新しい timeline/normal/transfer planning を開始する瞬間に呼び出す。
    // 古い session の sessionId を残すと query が再開して snackbar が再表示されるため、
    // sessionId をクリアすることで根本から無効化する。
    const clearNotAllocHint = useCallback(() => {
      setShowNotAllocSuggestionSnackbar(false);
      setNotAllocSessionId(null);
    }, []);

    // 初期値: /api/v3/latest_algorithm_request_version から取得（V3Timeline と同じ hook）
    // mutation/polling 完了時に setLatestAlgorithmRequestVersion で上書きされる
    const algorithmVersionQuery = useQueryLatestAlgorithmRequestVersion(startOn, endOn);
    const initialAlgorithmVersion = algorithmVersionQuery.data;
    // debounce 経由のキャンセル → 即再実行ケースで version が古いまま送ると 'すでに更新されています' 系のエラーになる。
    // timelinePlanning.mutate 直前で refetch して常に最新値を使えるよう、refetch への参照を ref で保持する。
    const algorithmVersionRefetchRef = useRef(algorithmVersionQuery.refetch);
    useEffect(() => {
      algorithmVersionRefetchRef.current = algorithmVersionQuery.refetch;
    }, [algorithmVersionQuery.refetch]);
    useEffect(() => {
      if (initialAlgorithmVersion != null) {
        setLatestAlgorithmRequestVersion(initialAlgorithmVersion);
      }
    }, [initialAlgorithmVersion, startOn, endOn]);

    // 自動配車エラー時の全体リセット（V3Plans の handleErrorResponse → resetQuery 相当）。
    // latest_algorithm_request_version がサーバ側で進んだケース（"同時にリクエストが行われた可能性があるため..."）
    // では、古い version を持ったままだと以降の自動配車がすべて失敗する。関連クエリを invalidate して最新化する。
    const resetAfterPlanningError = useCallback(() => {
      hasQueuedOverrideChangeRef.current = false;
      setOverrideRowCards(new Map());
      setOverrideUnassigned(null);
      setAsyncPlanningResponse(null);
      setRunningPlanningId(null);
      // エラー時はサーバ上でジョブがまだ実行中の可能性がある (通信断や一時的な 500 など)。
      // 楽観的に空へはせず invalidate で再取得し、実態がまだ running ならバナー/停止導線を残す。
      // 対象はジョブ開始時の日付 (実行中に表示日付を変えていても誤らない)。実行中なら必ずセット済み。
      const range = runningPlanningRangeRef.current;
      runningPlanningRangeRef.current = null;
      if (range) {
        void invalidateRunningPlanningsCache(queryClient, range.startOn, range.endOn);
      }
      void queryClient.invalidateQueries({ queryKey: ['useQueryLatestAlgorithmRequestVersion'] });
      void queryClient.invalidateQueries({ queryKey: [CONVENIENCE_DISPATCH_DELIVERIES_QUERY_KEY] });
      void queryClient.invalidateQueries({ queryKey: [CONVENIENCE_DISPATCH_ALL_DELIVERIES_QUERY_KEY] });
      void queryClient.invalidateQueries({ queryKey: [SHIPMENT_ORDER_LEG_RUNS_QUERY_KEY] });
    }, [queryClient]);
    // polling: enabled=false のときは呼ばれない（useQueryPlanningsResultsV5 は null チェック済）
    const { data: planningResultData, error: planningResultError } = useQueryPlanningsResultsV5(
      asyncPlanningResponse ?? (null),
    );

    // polling 完了時: キャッシュ invalidate → overrides クリア
    //
    // 注意: override をクリアするタイミングが deliveries の refetch 完了より早いと、
    // 古いキャッシュ + 空 override で一瞬 render されてユーザのドロップ位置が元の位置に
    // 戻って見えてしまう。そのため deliveries の refetch を await してから
    // override / legRuns 関連のキャッシュ更新を行うようにしている。
    useEffect(() => {
      if (!planningResultData && !planningResultError) return;
      if (planningResultData) {
        void (async () => {
          console.log('[convenience-dispatch] polling 完了', planningResultData);

          const notAllocIds = planningResultData.not_allocated_order_ids ?? [];
          // この handler 開始時の session_id を記録。await 中にユーザが新規操作 → cancel が
          // 走るとこの ref がクリアされるので、後続処理 (override クリア等) を中断する。
          const sessionAtStart = planningResultData.session_id ?? null;
          activePollingSessionRef.current = sessionAtStart;

          // version 更新は cache に影響しないので即時 OK
          setLatestAlgorithmRequestVersion(planningResultData.latest_algorithm_request_version ?? 0);
          void queryClient.invalidateQueries({ queryKey: ['useQueryLatestAlgorithmRequestVersion'] });

          // deliveries の refetch を await する。
          // この await 中は override が残ったままなので、ユーザのドロップ位置が表示され続け、
          // 元の位置に戻って見える flicker を防げる。
          try {
            await Promise.all([
              queryClient.invalidateQueries({ queryKey: [CONVENIENCE_DISPATCH_DELIVERIES_QUERY_KEY] }),
              queryClient.invalidateQueries({ queryKey: [CONVENIENCE_DISPATCH_ALL_DELIVERIES_QUERY_KEY] }),
            ]);
          } catch (e) {
            console.warn('[convenience-dispatch] deliveries refetch failed', e);
          }

          // await 中にキャンセル発生 (= ユーザが新規操作) を検知。残りの処理は中断する。
          // override クリアをスキップすることでユーザの新操作が消えない。
          if (activePollingSessionRef.current !== sessionAtStart) {
            console.log('[convenience-dispatch] polling handler cancelled mid-flight (session changed)');
            return;
          }

          // 未割当だった legRun の legRuns キャッシュを `assigned=false` に直接書き換える。
          // これで refetch 前でも未割当リストの source (serverUnassigned) に反映される。
          // V3Plans の plansContext.addOrderIds 相当の処理。
          // SHIPMENT_ORDER_LEG_RUNS_QUERY_KEY は「単体エンティティ cache」と「無限スクロール cache」の
          // 両方で使われているため、pages を持つ infinite query だけを対象に更新する。
          // 注: deliveries の refetch 後に行う (= serverUnassigned の reference 変更で
          // line 610 useEffect が override を clear する瞬間に server data が新鮮であるように)
          if (notAllocIds.length > 0) {
            const notAllocIdSet = new Set<number>(notAllocIds);
            queryClient.setQueriesData<InfiniteData<ShipmentOrderLegRunListResponse>>(
              { queryKey: [SHIPMENT_ORDER_LEG_RUNS_QUERY_KEY] },
              (old) => {
                if (!old) return old;
                const maybePages = (old as { pages?: unknown }).pages;
                if (!Array.isArray(maybePages)) return old;
                return {
                  ...old,
                  pages: old.pages.map((p) => ({
                    ...p,
                    data: p.data.map((lr) =>
                      (lr.id != null && notAllocIdSet.has(lr.id)) ? { ...lr, assigned: false } : lr,
                    ),
                  })),
                };
              },
            );
          }
          void queryClient.invalidateQueries({ queryKey: [SHIPMENT_ORDER_LEG_RUNS_QUERY_KEY] });

          // overrides はサーバデータに置き換える (refetch 完了後なので flicker しない)。
          // ただしこの job の実行中に追加操作が入っていれば、古い計算結果で
          // 操作途中のローカル配置を消さず、dirty 検知で次の timelinePlanning に回す。
          if (!shouldClearOverrideAfterPlanningResult(hasQueuedOverrideChangeRef.current)) {
            console.log('[convenience-dispatch] queued override を保持して次の自動配車へ回す');
          } else {
            setOverrideRowCards(new Map());
            setOverrideUnassigned(null);
          }

          // 未割当案件があればその件数と session_id を取得して「配車のヒント」表示に繋げる
          if (notAllocIds.length > 0 && planningResultData.session_id) {
            console.log('[convenience-dispatch] 未割当発生 → sessionId 保持', {
              sessionId: planningResultData.session_id,
              notAllocIds,
            });
            setNotAllocSessionId(planningResultData.session_id);
            enqueueSnackbar(
              `${notAllocIds.length.toLocaleString()}件が割り当たりませんでした。割当たらない理由を確認してください。`,
            );
          } else {
            setNotAllocSessionId(null);
            enqueueSnackbar('自動配車が完了しました', { variant: 'success' });
          }

          // 自動配車後、割り当たらなかった legRun を未割当パネル側で「選択中」に戻す。
          // unassigned 側のキャッシュ反映と React 描画タイミングの差で直接 set すると
          // 別 effect のフィルタに弾かれることがあるため、pending → 揃ってから確定の二段構え。
          setPendingNotAllocSelect(notAllocIds);

          setAsyncPlanningResponse(null);
          // 完了 → cancel API 用 ID もクリア (以降の操作は別 job として扱う)
          setRunningPlanningId(null);
          activePollingSessionRef.current = null;
          // 地図画面などと共有している running 一覧キャッシュを空にして、
          // 完了直後に遷移したとき「n分前から自動配車を実行しています」が一瞬残らないようにする。
          // 実行中に表示日付を変えていても、ジョブ開始時の日付を対象にする (現在表示日付ではない)。
          // polling 完了時点では必ずセット済みだが、無ければ対象日付が不明なのでクリアしない。
          const completedRange = runningPlanningRangeRef.current;
          runningPlanningRangeRef.current = null;
          if (completedRange) {
            void clearRunningPlanningsCache(queryClient, completedRange.startOn, completedRange.endOn);
          }
        })();
      } else if (planningResultError) {
        console.error('[convenience-dispatch] polling エラー', planningResultError);
        const msg = (planningResultError as { response?: { data?: { message?: string } }; message?: string })
          .response?.data?.message ?? 'エラーが発生しました';
        enqueueSnackbar(`自動配車失敗: ${msg}`, { variant: 'error' });
        resetAfterPlanningError();
      }
    }, [planningResultData, planningResultError, queryClient, enqueueSnackbar, resetAfterPlanningError]);

    // 実行中フラグ = mutation 中 or polling 中
    const isAutoDispatching = timelinePlanning.isLoading
      || normalPlanning.isLoading
      || transferPlanning.isLoading
      || operationBulkResetting.isLoading
      || asyncPlanningResponse !== null;

    // resetting 成功後、react-query キャッシュを直接書き換えて即座に UI 反映する（再取得なし）
    // - deliveries キャッシュ: リセット対象 legRun と、それを参照する operations を各 delivery から除去
    // - legRuns キャッシュ: 対象 legRun の `assigned=false, delivery_id=null` に書き換え（→ 未割当リストに現れる）
    const applyResetToCaches = useCallback((resetLegRunIds: number[]) => {
      const idSet = new Set(resetLegRunIds);

      queryClient.setQueriesData<InfiniteData<DeliveriesResponse>>(
        { queryKey: [CONVENIENCE_DISPATCH_DELIVERIES_QUERY_KEY] },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              data: page.data.map((d) => ({
                ...d,
                shipmentOrderLegRuns: d.shipmentOrderLegRuns.filter(
                  (lr) => lr.id == null || !idSet.has(lr.id),
                ),
                operations: d.operations.filter((op) => !idSet.has(op.shipmentOrderLegRunId)),
              })),
            })),
          };
        },
      );

      queryClient.setQueriesData<PlanningsDeliveryWithLegRunEntity[]>(
        { queryKey: [CONVENIENCE_DISPATCH_ALL_DELIVERIES_QUERY_KEY] },
        (old) => {
          if (!old) return old;
          return old.map((d) => ({
            ...d,
            shipmentOrderLegRuns: d.shipmentOrderLegRuns.filter(
              (lr) => lr.id == null || !idSet.has(lr.id),
            ),
            operations: d.operations.filter((op) => !idSet.has(op.shipmentOrderLegRunId)),
          }));
        },
      );

      // SHIPMENT_ORDER_LEG_RUNS_QUERY_KEY は「単体エンティティ cache」と「無限スクロール cache」の
      // 両方で使われているため、prefix match で単体 cache にもヒットする。
      // infinite query（pages を持つ）だけを対象に更新する。
      queryClient.setQueriesData<InfiniteData<ShipmentOrderLegRunListResponse>>(
        { queryKey: [SHIPMENT_ORDER_LEG_RUNS_QUERY_KEY] },
        (old) => {
          if (!old) return old;
          const maybePages = (old as { pages?: unknown }).pages;
          if (!Array.isArray(maybePages)) return old;
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              data: page.data.map((lr) => (
                lr.id != null && idSet.has(lr.id)
                  ? { ...lr, assigned: false, delivery_id: null as unknown as string }
                  : lr
              )),
            })),
          };
        },
      );
      // 上の setQueriesData は「既に loaded されているページの中の対象 legRun」しか更新できない。
      // OFF した車両の legRun が未ロードページにある場合、未割当リストに出てこなくなるので
      // refetch を強制発火する (loaded ページが server 最新値で再取得され、未割当として現れる)。
      void queryClient.invalidateQueries({ queryKey: [SHIPMENT_ORDER_LEG_RUNS_QUERY_KEY] });

      // resetting もサーバ側で version が進むので refetch させる
      void queryClient.invalidateQueries({ queryKey: ['useQueryLatestAlgorithmRequestVersion'] });
    }, [queryClient]);

    // 詳細パネルの「座標を編集」: 対象カードの全 legRun の waypoint と、
    // 同 (name, address) の Place を一括で更新する。
    const bulkUpdateWaypointLocation = useBulkUpdateWaypointLocationMutation();
    const handleGeoUpdate = useCallback(
      (payload: { newGeo: { lat: number; lng: number }; side: 'loading' | 'unloading'; legRunIds: number[] }) => {
        if (payload.legRunIds.length === 0) return;
        bulkUpdateWaypointLocation.mutate(
          {
            legRunIds: payload.legRunIds,
            side: payload.side,
            lat: payload.newGeo.lat,
            lng: payload.newGeo.lng,
          },
          {
            onSuccess: () => {
              enqueueSnackbar('座標を更新しました', { variant: 'success' });
              void queryClient.invalidateQueries({ queryKey: [CONVENIENCE_DISPATCH_DELIVERIES_QUERY_KEY] });
              void queryClient.invalidateQueries({ queryKey: [CONVENIENCE_DISPATCH_ALL_DELIVERIES_QUERY_KEY] });
              void queryClient.invalidateQueries({ queryKey: [SHIPMENT_ORDER_LEG_RUNS_QUERY_KEY] });
            },
            onError: (e: Error) => {
              const err = e as AxiosError<{ error?: string }>;
              enqueueSnackbar(err.response?.data?.error ?? '座標の更新に失敗しました', { variant: 'error' });
            },
          },
        );
      },
      [bulkUpdateWaypointLocation, enqueueSnackbar, queryClient],
    );

    // 車両 ON/OFF
    //   - OFF: shift_truck/shift ごと削除（紐づく leg_run の operations もリセットされる）
    //   - ON: 単純に UI 状態を ON に戻す（API 呼び出しなし）
    // ※ Shift/ShiftTruck まで消すのでスイッチ自体の意味は薄れるが、UI は別途調整予定
    const { destroy: destroyShiftTruck, update: updateShiftTruck } = useConvenienceDispatchShiftTruckMutation();
    const updateShiftTruckTruckNumber = useUpdateShiftTruckTruckNumberMutation();

    // 車両 / ドライバー / 車番 編集メニューの open state.
    // anchor + 対象 truckId をまとめて持つ
    const [truckEditState, setTruckEditState] = useState<{ anchor: HTMLElement; truckId: number } | null>(null);
    const [driverEditState, setDriverEditState] = useState<{ anchor: HTMLElement; truckId: number } | null>(null);
    const [plateEditState, setPlateEditState] = useState<{ anchor: HTMLElement; truckId: number; initialValue: string } | null>(null);
    const [plateInputValue, setPlateInputValue] = useState('');

    // 「他 row で利用中の truck/driver」セット (現 row は除外)
    const usedTruckIdsByOtherRows = useMemo(() => {
      if (!truckEditState) return new Set<number>();
      const set = new Set<number>();
      for (const g of rowGroups) {
        const tid = g.vehicle.truckId;
        if (tid != null && tid !== truckEditState.truckId) set.add(tid);
      }
      return set;
    }, [rowGroups, truckEditState]);
    const usedDriverIdsByOtherRows = useMemo(() => {
      if (!driverEditState) return new Set<number>();
      const set = new Set<number>();
      for (const g of rowGroups) {
        const tid = g.vehicle.truckId;
        const did = g.vehicle.driverId;
        if (did != null && tid !== driverEditState.truckId) set.add(did);
      }
      return set;
    }, [rowGroups, driverEditState]);

    const findVehicleByTruckId = useCallback((truckId: number) => (
      rowGroups.find((g) => g.vehicle.truckId === truckId)?.vehicle ?? null
    ), [rowGroups]);

    const handleTruckEdit = useCallback((truckId: number, anchor: HTMLElement) => {
      setTruckEditState({ anchor, truckId });
    }, []);
    const handleDriverEdit = useCallback((truckId: number, anchor: HTMLElement) => {
      setDriverEditState({ anchor, truckId });
    }, []);
    const handlePlateNumberEdit = useCallback((truckId: number, anchor: HTMLElement) => {
      const v = findVehicleByTruckId(truckId);
      const initial = v?.plateNumber ?? '';
      setPlateInputValue(initial);
      setPlateEditState({ anchor, truckId, initialValue: initial });
    }, [findVehicleByTruckId]);

    const refetchAfterEdit = useCallback(async () => {
      // useInfiniteQuery (staleTime: Infinity) のため明示 refetch
      await Promise.all([
        deliveriesQuery.refetch(),
        legRunsQuery.refetch(),
        allDeliveriesQuery.refetch(),
      ]);
    }, [deliveriesQuery, legRunsQuery, allDeliveriesQuery]);

    const handleTruckSelect = useCallback(async (newTruckId: number) => {
      const target = truckEditState ? findVehicleByTruckId(truckEditState.truckId) : null;
      setTruckEditState(null);
      if (!target?.shiftTruckId || target.truckId === newTruckId || target.truckId == null) return;
      const oldTruckId = target.truckId;
      const newTruck = trucks.find((t) => t.id === newTruckId);
      // 楽観的に表示更新: 新しい truck 名 / 車番 を即時反映 (mutation 完了+refetch を待たない)
      setVehicleOverride(oldTruckId, {
        truckName: newTruck?.licensePlateValue ?? '',
        plateNumber: newTruck?.truckNumber ?? '',
      });
      try {
        await updateShiftTruck.mutateAsync({ shiftTruckId: target.shiftTruckId, truckId: newTruckId });
        await refetchAfterEdit();
        enqueueSnackbar('車両を変更しました', { variant: 'success' });
      } catch {
        enqueueSnackbar('車両の変更に失敗しました', { variant: 'error' });
      } finally {
        // refetch 後はサーバ値が override と同じになっているはずなので clear で見た目変化なし。
        // 失敗時は元 (サーバ) 値に戻すために clear。
        clearVehicleOverride(oldTruckId);
      }
    }, [
      truckEditState, findVehicleByTruckId, trucks, updateShiftTruck, enqueueSnackbar,
      refetchAfterEdit, setVehicleOverride, clearVehicleOverride,
    ]);

    const handleDriverSelect = useCallback(async (newDriverId: number) => {
      const target = driverEditState ? findVehicleByTruckId(driverEditState.truckId) : null;
      setDriverEditState(null);
      if (!target?.shiftTruckId || target.driverId === newDriverId || target.truckId == null) return;
      const oldTruckId = target.truckId;
      const newDriver = drivers.find((d) => d.id === newDriverId);
      setVehicleOverride(oldTruckId, { driverName: newDriver?.name ?? '' });
      try {
        await updateShiftTruck.mutateAsync({ shiftTruckId: target.shiftTruckId, driverId: newDriverId });
        await refetchAfterEdit();
        enqueueSnackbar('ドライバーを変更しました', { variant: 'success' });
      } catch {
        enqueueSnackbar('ドライバーの変更に失敗しました', { variant: 'error' });
      } finally {
        clearVehicleOverride(oldTruckId);
      }
    }, [
      driverEditState, findVehicleByTruckId, drivers, updateShiftTruck, enqueueSnackbar,
      refetchAfterEdit, setVehicleOverride, clearVehicleOverride,
    ]);

    const handlePlateNumberSubmit = useCallback(async () => {
      const target = plateEditState ? findVehicleByTruckId(plateEditState.truckId) : null;
      const trimmed = plateInputValue.trim();
      setPlateEditState(null);
      if (!target || target.truckId == null) return;
      if (trimmed === plateEditState?.initialValue.trim()) return;
      // 配車前 (legRunIds.length === 0) でも shift_truck があれば編集可能。
      // shift_truck_id は同 truck の代表 delivery.id (= shift_truck.id) として解決する。
      const shiftTruckId = deliveries.find((d) => d.truckId === target.truckId)?.id;
      if (shiftTruckId == null) return;
      const oldTruckId = target.truckId;
      setVehicleOverride(oldTruckId, { plateNumber: trimmed });
      try {
        await updateShiftTruckTruckNumber.mutateAsync({ shiftTruckId, truckNumber: trimmed });
        await refetchAfterEdit();
        enqueueSnackbar('車番を更新しました', { variant: 'success' });
      } catch {
        enqueueSnackbar('車番の更新に失敗しました', { variant: 'error' });
      } finally {
        clearVehicleOverride(oldTruckId);
      }
    }, [
      plateEditState, plateInputValue, findVehicleByTruckId, deliveries,
      updateShiftTruckTruckNumber, enqueueSnackbar, refetchAfterEdit,
      setVehicleOverride, clearVehicleOverride,
    ]);
    const handleToggleVehicleEnabled = useCallback((truckId: number) => {
      const group = serverRowGroups.find((g) => g.rows.some((r) => r.truckId === truckId));
      const delivery = deliveries.find((d) => d.truckId === truckId);
      const shiftTruckId = delivery?.id;
      if (shiftTruckId == null) return; // delivery が無い vehicle はそもそも表示されない

      const legRunIds = group
        ? Array.from(
            new Set(group.rows.flatMap((r) => getMatchedLegRunIdsInRow(r.source))),
          )
        : [];

      const description = legRunIds.length > 0
        ? `${group?.vehicle.vehicleNumber ?? ''} を OFF にすると、勤務計画が削除され、`
          + `割り当てられている ${legRunIds.length} 件の案件は未割当に戻ります。`
        : `${group?.vehicle.vehicleNumber ?? ''} の勤務計画を削除します。`;

      setConfirmDialog({
        title: '車両をOFFにしますか？',
        description,
        onConfirm: async () => {
          let apiSucceeded = false;
          try {
            await destroyShiftTruck.mutateAsync(shiftTruckId);
            apiSucceeded = true;
          } catch (e) {
            const err = e as AxiosError<{ message: string }>;
            enqueueSnackbar(err.response?.data?.message ?? '処理に失敗しました', { variant: 'error' });
          }
          if (apiSucceeded) {
            // legRun のキャッシュも assigned=false に書き換えて即時反映
            if (legRunIds.length > 0) applyResetToCaches(legRunIds);
            // 削除された delivery (= shift_truck) 自体もキャッシュから除去
            // refetch を待たずに左リストの行をすぐ消す
            queryClient.setQueriesData<InfiniteData<DeliveriesResponse>>(
              { queryKey: [CONVENIENCE_DISPATCH_DELIVERIES_QUERY_KEY] },
              (old) => {
                if (!old) return old;
                return {
                  ...old,
                  pages: old.pages.map((page) => ({
                    ...page,
                    data: page.data.filter((d) => d.id !== shiftTruckId),
                  })),
                };
              },
            );
            queryClient.setQueriesData<PlanningsDeliveryWithLegRunEntity[]>(
              { queryKey: [CONVENIENCE_DISPATCH_ALL_DELIVERIES_QUERY_KEY] },
              (old) => {
                if (!old) return old;
                return old.filter((d) => d.id !== shiftTruckId);
              },
            );
            enqueueSnackbar(`${group?.vehicle.vehicleNumber ?? '車両'} を OFF にしました`, { variant: 'success' });
          }
        },
      });
    }, [serverRowGroups, deliveries, destroyShiftTruck, enqueueSnackbar, applyResetToCaches, queryClient]);

    // 回転追加: ローカル state に次回転を追加するだけ
    const handleAddRotation = useCallback((truckId: number) => {
      setExtraRotations((prev) => {
        const next = new Map(prev);
        const serverMax = Math.max(
          0,
          ...sourceRowsRaw.filter((r) => r.truckId === truckId).map((r) => r.rotationNumber),
        );
        const existing = next.get(truckId) ?? [];
        const localMax = Math.max(0, ...existing);
        const nextRotation = Math.max(serverMax, localMax) + 1;
        next.set(truckId, [...existing, nextRotation]);
        return next;
      });
    }, [sourceRowsRaw]);

    // 選択中の行 (= 配送計画) をまとめて削除する。
    // ・確認時には 「n件の配送計画を削除します」 (n = 選択行数)
    // ・完了時には 「m件の配送計画を削除しました」
    //   m は実際に削除対象になった行数 (= legRun (案件) を持つ行のみ)。
    //   3 行選択していて 1 行に案件が無い場合 → n=3, m=2 となる。
    const handleUnassignSelected = useCallback(() => {
      if (selectedRowIds.size === 0) return;

      const selectedCount = selectedRowIds.size;
      const legRunIds = new Set<number>();
      let deletableRowCount = 0; // m: 案件を持つ行 (実際に削除される行)
      serverRowGroups.forEach((g) => g.rows.forEach((r) => {
        if (!selectedRowIds.has(r.rowId)) return;
        const rowLegRunIds = getMatchedLegRunIdsInRow(r.source);
        if (rowLegRunIds.length > 0) {
          deletableRowCount += 1;
          rowLegRunIds.forEach((id) => legRunIds.add(id));
        }
      }));
      const ids = Array.from(legRunIds);

      const exec = async () => {
        let apiSucceeded = false;
        if (ids.length > 0) {
          try {
            await operationBulkResetting.mutateAsync(ids);
            apiSucceeded = true;
          } catch (e) {
            const err = e as AxiosError<{ message: string }>;
            enqueueSnackbar(err.response?.data?.message ?? '処理に失敗しました', { variant: 'error' });
          }
        } else {
          // 削除対象 (案件付き行) が無くても選択クリアと結果表示は行う
          apiSucceeded = true;
        }
        if (apiSucceeded) {
          if (ids.length > 0) applyResetToCaches(ids);
          setSelectedRowIds(new Set());
          enqueueSnackbar(`${deletableRowCount}件の配送計画を削除しました`, { variant: 'success' });
        }
      };

      setConfirmDialog({
        title: '配送計画を削除しますか？',
        description: `${selectedCount}件の配送計画を削除します。`,
        onConfirm: exec,
      });
    }, [selectedRowIds, serverRowGroups, operationBulkResetting, enqueueSnackbar, applyResetToCaches]);

    // 回転削除: ローカル追加分なら state から削除、サーバ分なら API で未割当に戻す
    const handleDeleteRotation = useCallback((truckId: number, rotationNumber: number) => {
      const isVirtual = (extraRotations.get(truckId) ?? []).includes(rotationNumber);
      if (isVirtual) {
        setExtraRotations((prev) => {
          const next = new Map(prev);
          const filtered = (next.get(truckId) ?? []).filter((rn) => rn !== rotationNumber);
          if (filtered.length === 0) next.delete(truckId);
          else next.set(truckId, filtered);
          return next;
        });
        return;
      }
      const group = serverRowGroups.find((g) => g.rows.some((r) => r.truckId === truckId));
      const row = group?.rows.find((r) => r.truckId === truckId && r.rotationNumber === rotationNumber);
      if (!row) return;
      // /api/v5/plannings/resetting の order_ids は shipment_order_leg_run.id
      const legRunIds = getMatchedLegRunIdsInRow(row.source);

      // 案件 (実 operation 由来 legRun) のない placeholder row はサーバに送るものが無いので
      // ローカルで非表示にするだけ。日付変更 or 呼び出し / 対応づけ再実行で復活する。
      if (legRunIds.length === 0) {
        setHiddenPlaceholderRotations((prev) => {
          const next = new Map(prev);
          const current = new Set(next.get(truckId) ?? []);
          current.add(rotationNumber);
          next.set(truckId, current);
          return next;
        });
        enqueueSnackbar(`${rotationNumber}回転目を非表示にしました`, { variant: 'success' });
        return;
      }

      const exec = async () => {
        let apiSucceeded = false;
        try {
          await operationBulkResetting.mutateAsync(legRunIds);
          apiSucceeded = true;
        } catch (e) {
          const err = e as AxiosError<{ message: string }>;
          enqueueSnackbar(err.response?.data?.message ?? '処理に失敗しました', { variant: 'error' });
        }
        if (apiSucceeded) {
          applyResetToCaches(legRunIds);
          enqueueSnackbar(`${rotationNumber}回転目を削除し ${legRunIds.length} 件の案件を未割当に戻しました`, { variant: 'success' });
        }
      };
      setConfirmDialog({
        title: 'この回転を削除しますか？',
        description: `${rotationNumber}回転目を削除します。${legRunIds.length} 件の案件が未割当に戻ります。`,
        onConfirm: exec,
      });
    }, [extraRotations, serverRowGroups, operationBulkResetting, enqueueSnackbar, applyResetToCaches]);

    void rowIdFromTruck;

    // ─────────────────────────────────────────────────────────────
    // 自動配車（操作アイドル検知 + debounce 3秒 + timelinePlanning）
    // ─────────────────────────────────────────────────────────────
    // rowId → shift_truck_id (= delivery.id) マップ。サーバ既存の delivery を対象。
    // - operations が populated な delivery: 全 cycle を登録
    // - operations が空のシフトのみ delivery: rotation=1 に対応させる
    //   （車両追加直後の空枠もそのトラックの 1 回転目 row として使われるため）
    const rowIdToShiftTruckId = useMemo(() => {
      const m = new Map<string, number>();
      deliveries.forEach((d) => {
        if (d.operations.length === 0) {
          m.set(rowIdFromTruck(d.truckId, 1), d.id);
          return;
        }
        const cycles = new Set(d.operations.map((op) => (op.cycle >= 1 ? op.cycle : 1)));
        cycles.forEach((cycle) => {
          m.set(rowIdFromTruck(d.truckId, cycle), d.id);
        });
      });
      return m;
    }, [deliveries]);

    // truckId → delivery.id: ローカルで追加した仮想回転 (extraRotations) 用に
    // そのトラックの代表 delivery を引けるようにする。
    const truckIdToDeliveryId = useMemo(() => {
      const m = new Map<number, number>();
      deliveries.forEach((d) => {
        if (!m.has(d.truckId)) m.set(d.truckId, d.id);
      });
      return m;
    }, [deliveries]);

    const runAutoDispatch = useCallback(async () => {
      // 仮想回転(新規追加) は shift_truck_id 未確定を表す marker
      const VIRTUAL_STID = -1;

      if (isAutoDispatching) {
        console.log('[convenience-dispatch] runAutoDispatch skip: 実行中');
        return;
      }
      if (overrideRowCards.size === 0 && overrideUnassigned === null) {
        console.log('[convenience-dispatch] runAutoDispatch skip: override 空');
        return;
      }
      // ここからは現在の override を新しい job の入力として扱う。
      // 以降に追加操作が入った場合は override 監視 effect が再度 true にする。
      hasQueuedOverrideChangeRef.current = false;
      console.log('[convenience-dispatch] runAutoDispatch start', {
        overrideRows: overrideRowCards.size,
        overrideUnassigned: overrideUnassigned?.length ?? null,
      });

      // legRun の所在は (stid, cycle) のペアで表す。
      // - stid: サーバ既存 delivery の id / VIRTUAL_STID (-1) / null (未割当)
      // - cycle: 回転番号（未割当時は null）
      // cycle 情報を含めることで、同一 truck 内での回転移動も検知できる。
      type LegRunLocation = { stid: number | null; cycle: number | null };

      // 元の legRun → 所在マップ（既存 operation のある legRun のみ）
      const originalLocation = new Map<number, LegRunLocation>();
      deliveries.forEach((d) => {
        d.operations.forEach((op) => {
          const lrId = op.shipmentOrderLegRunId;
          // 同じ legRun の op が複数ある場合、最初の cycle を採用（全て同じ cycle であるはず）
          if (!originalLocation.has(lrId)) {
            originalLocation.set(lrId, { stid: d.id, cycle: op.cycle >= 1 ? op.cycle : 1 });
          }
        });
      });

      // 現在（override 反映後）の legRun → 所在マップ
      const currentLocation = new Map<number, LegRunLocation>();
      const currentUnassigned = overrideUnassigned ?? serverUnassigned;
      currentUnassigned.forEach((u) => {
        const m = /^lr-(\d+)$/.exec(u.id);
        if (m) {
          const lrId = parseInt(m[1], 10);
          if (!Number.isNaN(lrId)) currentLocation.set(lrId, { stid: null, cycle: null });
        }
      });
      rowGroups.forEach((g) => g.rows.forEach((r) => {
        // 仮想回転 (回転追加) は対応する delivery.id が rowIdToShiftTruckId に無いが、
        // 同じ truckId に既存 delivery があればそこへ new cycle として寄せる
        const stid = rowIdToShiftTruckId.get(r.rowId)
          ?? truckIdToDeliveryId.get(r.truckId)
          ?? VIRTUAL_STID;
        const cycle = r.rotationNumber;
        r.cards.forEach((c) => {
          // 所在判定には delivery カード（降地）のみを用いる。
          // loading カードにも同じ legRunId が含まれているため、両方を見ると別トラックへの
          // 移動時に「移動元の loading」と「移動先の delivery」で currentLocation が上書き合戦になる。
          if (c.type !== 'delivery') return;
          // no-delivery / fr-template は「まだ割り当てられていない」状態なので current location 対象外
          if (c.status === 'no-delivery' || c.status === 'fr-template') return;
          (c.legRunIds ?? []).forEach((lrId) => {
            currentLocation.set(lrId, { stid, cycle });
          });
        });
      }));

      // 変更があった legRun を特定（stid または cycle が変わったもの）
      const allLegRunIds = new Set<number>();
      originalLocation.forEach((_, id) => allLegRunIds.add(id));
      currentLocation.forEach((_, id) => allLegRunIds.add(id));
      const changedLegRunIds = new Set<number>();
      allLegRunIds.forEach((lrId) => {
        const orig = originalLocation.get(lrId);
        const now = currentLocation.get(lrId);
        const origStid = orig?.stid ?? null;
        const nowStid = now?.stid ?? null;
        const origCycle = orig?.cycle ?? null;
        const nowCycle = now?.cycle ?? null;
        if (origStid !== nowStid || origCycle !== nowCycle) changedLegRunIds.add(lrId);
      });

      // 同一 (stid, cycle) 内での順序変更も検知する。stid / cycle 変化が無くても、
      // override の表示順が server の operations の順序と異なれば「順序変更あり」として
      // その row 内の全 legRun を changed 扱いにする (timelinePlanning に並び順を伝える)。
      rowGroups.forEach((g) => g.rows.forEach((r) => {
        const stid = rowIdToShiftTruckId.get(r.rowId) ?? truckIdToDeliveryId.get(r.truckId) ?? VIRTUAL_STID;
        if (stid === VIRTUAL_STID) return;
        const cycle = r.rotationNumber;
        const currentOrder: number[] = [];
        r.cards.forEach((c) => {
          if (c.type !== 'delivery') return;
          if (c.status === 'no-delivery' || c.status === 'fr-template') return;
          (c.legRunIds ?? []).forEach((lrId) => {
            if (!currentOrder.includes(lrId)) currentOrder.push(lrId);
          });
        });
        if (currentOrder.length === 0) return;
        // server 側: 同じ (stid, cycle) に属する 降 op を arrival_at 順で並べた legRun id 列
        const delivery = deliveries.find((d) => d.id === stid);
        if (!delivery) return;
        const serverOrder: number[] = delivery.operations
          .filter((op) => op.action === '降' && (op.cycle >= 1 ? op.cycle : 1) === cycle)
          .slice()
          .sort((a, b) => new Date(a.arrivalAt).getTime() - new Date(b.arrivalAt).getTime())
          .map((op) => op.shipmentOrderLegRunId)
          .filter((id, idx, arr) => arr.indexOf(id) === idx);
        if (serverOrder.length === 0) return;
        const sameOrder = currentOrder.length === serverOrder.length
          && currentOrder.every((id, i) => id === serverOrder[i]);
        if (!sameOrder) {
          currentOrder.forEach((id) => changedLegRunIds.add(id));
        }
      }));

      console.log('[convenience-dispatch] changedLegRunIds', Array.from(changedLegRunIds));
      if (changedLegRunIds.size === 0) {
        console.log('[convenience-dispatch] runAutoDispatch skip: 変更 legRun 無し');
        return;
      }

      // 「row → unassigned」(配車済み → 未割当に戻す) ドロップを抽出して operationBulkResetting を呼ぶ。
      // これにより timelinePlanning が同 legRun を再割当するのを防ぎ、
      // 「未割当に戻す」ボタン押下と同等の挙動になる。
      const unassignedDropIds: number[] = [];
      changedLegRunIds.forEach((lrId) => {
        const orig = originalLocation.get(lrId);
        const now = currentLocation.get(lrId);
        // 元は配車済 (stid > 0) → 現在は unassigned (stid == null)
        if (orig?.stid != null && orig.stid > 0 && (now == null || now.stid == null)) {
          unassignedDropIds.push(lrId);
        }
      });
      if (unassignedDropIds.length > 0) {
        console.log('[convenience-dispatch] row→unassigned 検出 → operationBulkResetting', unassignedDropIds);
        try {
          await operationBulkResetting.mutateAsync(unassignedDropIds);
          applyResetToCaches(unassignedDropIds);
        } catch (e) {
          console.error('[convenience-dispatch] operationBulkResetting 失敗', e);
          enqueueSnackbar('未割当への変更を反映できませんでした', { variant: 'error' });
          resetAfterPlanningError();
          return;
        }
        // 処理済みの legRun は changedLegRunIds から外して timelinePlanning の対象から除外
        unassignedDropIds.forEach((id) => changedLegRunIds.delete(id));
        if (changedLegRunIds.size === 0) {
          console.log('[convenience-dispatch] 純 unassign のみ → timelinePlanning スキップ');
          // override をクリアして次のサーバ refetch とちらつき無く同期
          hasQueuedOverrideChangeRef.current = false;
          setOverrideRowCards(new Map());
          setOverrideUnassigned(null);
          return;
        }
      }

      // 仮想回転に割当てられた legRun は timeline に送れない（backend delivery 未確定）
      const virtualChangedIds = Array.from(changedLegRunIds).filter(
        (lrId) => currentLocation.get(lrId)?.stid === VIRTUAL_STID,
      );
      if (virtualChangedIds.length > 0) {
        console.warn('[convenience-dispatch] 仮想回転の割当は backend に反映不可（spec調整要）', virtualChangedIds);
      }

      // 影響を受けた delivery (stid) の集合: 変更 legRun の 移動元 / 移動先 を union
      // 影響を受けた delivery については (cycle 順 → 積→降 順) で全 ops を再構築する
      const affectedStids = new Set<number>();
      changedLegRunIds.forEach((lrId) => {
        const orig = originalLocation.get(lrId);
        const current = currentLocation.get(lrId);
        if (orig?.stid != null && orig.stid > 0) affectedStids.add(orig.stid);
        if (current?.stid != null && current.stid > 0) affectedStids.add(current.stid);
      });

      // 既存 operation の (lrId, action) → {id, stid, cycle} 索引。changed 判定と id 引き継ぎに使う
      const existingOpKeyOf = (lrId: number, action: string) => `${lrId}-${action}`;
      const existingOpIndex = new Map<string, { id: number; stid: number; cycle: number }>();
      deliveries.forEach((d) => {
        d.operations.forEach((op) => {
          existingOpIndex.set(existingOpKeyOf(op.shipmentOrderLegRunId, op.action), {
            id: op.id,
            stid: d.id,
            cycle: op.cycle >= 1 ? op.cycle : 1,
          });
        });
      });

      // timeline_operations 構築
      // 仕様: 影響 delivery については (stid, cycle) 単位で表示順に並べ、各グループ内で 積→降 を
      //       時系列が単調増加するように再生成する。例:
      //       移動前 cycle1=[A積,B積,C積,A降,B降,C降]
      //       A を cycle2 にドロップ後の display: cycle1=[B,C], cycle2=[A]
      //       → request: [B積, C積, B降, C降, A積, A降] (cycle1 全て → cycle2 全て、時刻も単調)
      const fallbackStart = new Date(`${startOn}T00:00:00+09:00`);
      const lastDepartureByStid = new Map<number, Date>();
      const timelineEntities: TimelineRequestEntity[] = [];

      rowGroups.forEach((g) => g.rows.forEach((r) => {
        // 仮想回転は truckId の代表 delivery.id にフォールバック
        const stid = rowIdToShiftTruckId.get(r.rowId) ?? truckIdToDeliveryId.get(r.truckId);
        if (stid == null) return;
        if (!affectedStids.has(stid)) return;
        const cycle = r.rotationNumber;

        // 当該 row の delivery カード列から legRun を表示順で抽出 (重複除外)
        const seen = new Set<number>();
        const legRunsInOrder: ShipmentOrderLegRunForPlanEntity[] = [];
        r.cards.forEach((c) => {
          if (c.type !== 'delivery') return;
          if (c.status === 'no-delivery' || c.status === 'fr-template') return;
          (c.legRunIds ?? []).forEach((lrId) => {
            if (seen.has(lrId)) return;
            const lr = legRuns.find((x) => x.id === lrId);
            if (!lr) return;
            seen.add(lrId);
            legRunsInOrder.push(lr);
          });
        });
        if (legRunsInOrder.length === 0) return;

        // baseline: 同 stid の前 cycle の最終 departure + 30分 → なければ delivery 開始 → fallback
        const prior = lastDepartureByStid.get(stid);
        const delivery = deliveries.find((d) => d.id === stid);
        const baseline = prior
          ? addSecondsToDate(prior, TRAVEL_SECONDS_BETWEEN_PLACES)
          : (delivery ? new Date(delivery.startAt) : fallbackStart);

        const ops = computeOpsForCycle({ stid, legRuns: legRunsInOrder, cycle, baseline });
        if (ops.length === 0) return;

        // 次 cycle の baseline 用に最終 departure を track
        const last = ops.reduce(
          (max, o) => (o.departure_at.getTime() > max.getTime() ? o.departure_at : max),
          ops[0].departure_at,
        );
        lastDepartureByStid.set(stid, last);

        // TimelineRequestEntity に変換 (id 引き継ぎ + changed 判定)
        ops.forEach((op) => {
          const existing = existingOpIndex.get(existingOpKeyOf(op.order_id, op.action));
          const isChanged = !existing || existing.stid !== stid || existing.cycle !== cycle;
          timelineEntities.push({
            id: existing?.id ?? 0,
            shift_truck_id: stid,
            order_id: op.order_id,
            arrival_at: op.arrival_at,
            departure_at: op.departure_at,
            action: op.action,
            cycle,
            changed: isChanged,
          });
        });
      }));

      // 空になった delivery の id
      // - 元から operations が空の delivery は除外（シフトのみの配車枠）
      // - 元は legRun を持っていたが、現在 current Location に誰も居ない delivery だけ対象
      const originallyPopulatedDeliveryIds = new Set<number>();
      deliveries.forEach((d) => {
        if (d.operations.length > 0) originallyPopulatedDeliveryIds.add(d.id);
      });
      const deliveryIdsWithOps = new Set<number>();
      currentLocation.forEach((loc) => {
        if (loc.stid != null && loc.stid > 0) deliveryIdsWithOps.add(loc.stid);
      });
      const emptyDeliveryIds: number[] = [];
      originallyPopulatedDeliveryIds.forEach((id) => {
        if (!deliveryIdsWithOps.has(id)) emptyDeliveryIds.push(id);
      });

      console.log('[convenience-dispatch] request', {
        timelineEntities: timelineEntities.length,
        emptyDeliveryIds: emptyDeliveryIds.length,
        virtualChangedIds,
      });
      if (timelineEntities.length === 0 && emptyDeliveryIds.length === 0) {
        console.log('[convenience-dispatch] runAutoDispatch skip: timeline/empty とも空');
        return;
      }

      enqueueSnackbar('自動配車を実行中...');
      clearNotAllocHint();

      // mutate 直前に最新 version を取得して送信する。
      // debounce + キャンセル → 再実行のシナリオで、前回の自動配車が server 側で進めた
      // version が反映される前に古い値で再 mutate するとバージョン不整合エラーになるため。
      let freshVersion = latestAlgorithmRequestVersion;
      try {
        const refresh = await algorithmVersionRefetchRef.current();
        if (typeof refresh.data === 'number') {
          freshVersion = refresh.data;
          setLatestAlgorithmRequestVersion(refresh.data);
        }
      } catch (e) {
        console.warn('[convenience-dispatch] failed to refetch latest algorithm version (use cached)', e);
      }

      timelinePlanning.mutate(
        {
          startOn,
          endOn,
          latestAlgorithmRequestVersion: freshVersion,
          timelineEntities,
          emptyDeliveryIds,
        },
        {
          onSuccess: (res: AxiosResponse<AsyncPlanningResponseEntity>) => {
            if (res.data?.status === 'queued' && res.data?.session_id) {
              console.log('[convenience-dispatch] mutation 成功 → polling 開始', res.data);
              setAsyncPlanningResponse(res.data);
              // 完了時のキャッシュ操作に使うため、ジョブ開始時の対象日付を記録する。
              runningPlanningRangeRef.current = { startOn, endOn };
              // キャンセル API 用に Planning ID を取得 (DELETE /api/v3/plannings/running は id 必須)
              void (async () => {
                try {
                  const refresh = await planningRunningsRefetchRef.current();
                  const running = refresh.data?.[0];
                  if (running?.id != null) {
                    setRunningPlanningId(running.id);
                  }
                } catch (e) {
                  console.warn('[convenience-dispatch] failed to fetch running planning id', e);
                }
              })();
            } else {
              console.log('[convenience-dispatch] mutation 即時完了', res.data);
              // 共有 running 一覧キャッシュを空にして、完了直後の画面遷移で「実行中」バナーが残らないようにする。
              void clearRunningPlanningsCache(queryClient, startOn, endOn);
              void queryClient.invalidateQueries({ queryKey: [CONVENIENCE_DISPATCH_DELIVERIES_QUERY_KEY] });
              void queryClient.invalidateQueries({ queryKey: [CONVENIENCE_DISPATCH_ALL_DELIVERIES_QUERY_KEY] });
              void queryClient.invalidateQueries({ queryKey: [SHIPMENT_ORDER_LEG_RUNS_QUERY_KEY] });
              // サーバ側で version が進むので refetch して state を更新
              void queryClient.invalidateQueries({ queryKey: ['useQueryLatestAlgorithmRequestVersion'] });
              enqueueSnackbar('自動配車が完了しました', { variant: 'success' });
            }
          },
          onError: (e: AxiosError<{ message?: string }>) => {
            console.error('[convenience-dispatch] mutation エラー', e);
            enqueueSnackbar(e.response?.data?.message ?? '自動配車でエラーが発生しました', { variant: 'error' });
            resetAfterPlanningError();
          },
        },
      );
    }, [
      isAutoDispatching,
      overrideRowCards,
      overrideUnassigned,
      deliveries,
      legRuns,
      rowGroups,
      rowIdToShiftTruckId,
      truckIdToDeliveryId,
      serverUnassigned,
      timelinePlanning,
      startOn,
      endOn,
      latestAlgorithmRequestVersion,
      enqueueSnackbar,
      queryClient,
      resetAfterPlanningError,
      clearNotAllocHint,
      operationBulkResetting,
      applyResetToCaches,
    ]);

    // 最新 runAutoDispatch を ref 経由で参照（debounce 関数から安定呼び出し）
    const runAutoDispatchRef = useRef(runAutoDispatch);
    useEffect(() => {
      runAutoDispatchRef.current = runAutoDispatch;
    }, [runAutoDispatch]);

    const debouncedAutoDispatch = useMemo(
      () => debounce(() => {
        console.log('[convenience-dispatch] debounce 発火 → runAutoDispatch 実行');
        void runAutoDispatchRef.current();
      }, 2000),
      [],
    );

    // override 変更を検知。実行中の自動配車があれば即キャンセルして新規 debounce を開始する。
    // 「直前の job は無駄なので捨て、最新状態で再計算する」というポリシー (Q2-A)。
    // 初回マウント時の override = 空 では発火させない (prev=null check)
    const prevOverrideRef = useRef<{ r: typeof overrideRowCards; u: typeof overrideUnassigned } | null>(null);
    useEffect(() => {
      const prev = prevOverrideRef.current;
      const overrideChanged = prev !== null
        && (prev.r !== overrideRowCards || prev.u !== overrideUnassigned);
      prevOverrideRef.current = { r: overrideRowCards, u: overrideUnassigned };
      // 「実行中」の判定: asyncPlanningResponse がある (polling 中) または
      //                   runningPlanningId が取れている (refetch 後の遅れて入ってきた ID も拾う)。
      const isInFlight = asyncPlanningResponse !== null || runningPlanningId !== null;
      if (overrideChanged && isInFlight) {
        hasQueuedOverrideChangeRef.current = true;
        console.log('[convenience-dispatch] running 中に override 変更 → queued + cancel');
        cancelRunningIfAny();
      }
    }, [overrideRowCards, overrideUnassigned, asyncPlanningResponse, runningPlanningId, cancelRunningIfAny]);

    // dirty 状態になった瞬間に debounce を kick。以降のユーザー操作で延長される
    useEffect(() => {
      const isDirty = overrideRowCards.size > 0 || overrideUnassigned !== null;
      if (isDirty && !isAutoDispatching) {
        console.log('[convenience-dispatch] dirty 検知 → debounce 開始');
        debouncedAutoDispatch();
      } else {
        debouncedAutoDispatch.cancel();
      }
    }, [overrideRowCards, overrideUnassigned, isAutoDispatching, debouncedAutoDispatch]);

    // ユーザー操作中は debounce をリセットして自動発火を遅らせる
    useEffect(() => {
      const handler = () => {
        const isDirty = overrideRowCards.size > 0 || overrideUnassigned !== null;
        if (isDirty && !isAutoDispatching) {
          debouncedAutoDispatch();
        }
      };
      const eventTypes = ['mousedown', 'mousemove', 'mouseup', 'keydown', 'keyup', 'touchstart', 'touchmove', 'touchend'];
      eventTypes.forEach((t) => document.addEventListener(t, handler, { passive: true }));
      return () => {
        eventTypes.forEach((t) => document.removeEventListener(t, handler));
      };
    }, [debouncedAutoDispatch, overrideRowCards, overrideUnassigned, isAutoDispatching]);

    // unmount 時にキャンセル
    useEffect(() => () => { debouncedAutoDispatch.cancel(); }, [debouncedAutoDispatch]);

    // 選択されたカード ID（card クリック時用）
    const [selectedCardIds, setSelectedCardIds] = useState<Set<string>>(new Set());
    // 詳細パネル (route-planner と同じ構造: selection + clickRect)
    const [detailSelection, setDetailSelection] = useState<DispatchSelection>(null);
    const [detailClickRect, setDetailClickRect] = useState<{
      top: number;
      left: number;
      right: number;
      width: number;
      height: number;
    } | null>(null);
    const detailOpen = detailSelection !== null;
    const [moveMenuAnchor, setMoveMenuAnchor] = useState<HTMLElement | null>(null);
    const closeDetail = useCallback(() => {
      setDetailSelection(null);
      setDetailClickRect(null);
      setSelectedCardIds(new Set());
      setMoveMenuAnchor(null);
    }, []);

    // 手動分割ダイアログ用 state
    const [splitTargetLegRun, setSplitTargetLegRun] = useState<ShipmentOrderLegRunForPlanEntity | null>(null);
    const [splitDialogOpen, setSplitDialogOpen] = useState(false);

    const closeSplitDialog = useCallback(() => {
      setSplitDialogOpen(false);
      setSplitTargetLegRun(null);
    }, []);

    const handleOpenSplitDialog = useCallback(() => {
      if (detailSelection?.source !== 'unassigned') return;
      const rawId = detailSelection.item.id;
      const legRunId = rawId.startsWith('lr-') ? parseInt(rawId.slice(3), 10) : NaN;
      if (Number.isNaN(legRunId)) return;
      const entity = legRunMaterialsMap.get(legRunId);
      if (!entity) return;
      setSplitTargetLegRun(entity);
      setSplitDialogOpen(true);
    }, [detailSelection, legRunMaterialsMap]);

    const handleSplitSuccess = useCallback(() => {
      void legRunsQuery.refetch();
    }, [legRunsQuery]);

    const handleRestoreSplit = useCallback((item: UnassignedItem) => {
      const rawId = item.id;
      const legRunId = rawId.startsWith('lr-') ? parseInt(rawId.slice(3), 10) : NaN;
      if (Number.isNaN(legRunId)) return;
      setConfirmDialog({
        title: '分割した案件を復元しますか？',
        description: '分割した案件を元に戻します。よろしいですか？',
        onConfirm: () => {
          restoreSplitLegRun.mutate(legRunId, {
            onSuccess: () => {
              enqueueSnackbar('分割した案件を元に戻しました', { variant: 'success' });
              void legRunsQuery.refetch();
            },
            onError: () => {
              enqueueSnackbar('分割した案件を元に戻す処理でエラーが発生しました', { variant: 'error' });
            },
          });
        },
      });
    }, [restoreSplitLegRun, enqueueSnackbar, legRunsQuery, setConfirmDialog]);

    const onSelectCard = useCallback((
      card: DispatchCard,
      vehicle: Vehicle,
      orderIndex: number,
      event: MouseEvent<HTMLDivElement> | KeyboardEvent<HTMLDivElement>,
    ) => {
      // 固定ルートの土台 (fr-template = 未割当 legRun 候補 / no-delivery = 本日なし) は
      // 本物の案件ではないので、クリックしても詳細パネル / 選択ハイライトを開かない。
      // ドラッグ不可と合わせて完全に「触れない」UI に揃える。
      if (
        card.type === 'delivery'
        && (card.status === 'fr-template' || card.status === 'no-delivery')
      ) {
        return;
      }
      // 同じカードをもう一度クリックしたらポップアップと選択表示を閉じる
      setSelectedCardIds((prev) => {
        if (prev.size === 1 && prev.has(card.id)) {
          setDetailSelection(null);
          setDetailClickRect(null);
          return new Set();
        }
        return new Set([card.id]);
      });
      // 既に同じカードが開いていたら閉じる、そうでなければそのカードのポップアップを開く
      if (detailSelection?.source === 'vehicle' && detailSelection.card.id === card.id) {
        return;
      }
      const target = event.currentTarget as HTMLElement | null;
      if (target) {
        const rect = target.getBoundingClientRect();
        setDetailClickRect({
          top: rect.top,
          left: rect.left,
          right: rect.right,
          width: rect.width,
          height: rect.height,
        });
      } else {
        setDetailClickRect(null);
      }
      setDetailSelection({ source: 'vehicle', card, vehicle, orderIndex });
    }, [detailSelection]);

    const [selectedUnassignedIds, setSelectedUnassignedIds] = useState<Set<string>>(new Set());
    const onSelectUnassigned = useCallback((id: string) => {
      setSelectedUnassignedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    }, []);

    // 自動配車後に「割り当たらなかった案件 (notAllocIds)」を選択状態へ復元するための pending state。
    // 直接 setSelectedUnassignedIds で入れると、別 useEffect で「unassigned に存在しない id を弾く」
    // フィルタが先に走って消えるタイミングがあるため、この pending を経由して
    // unassigned に notAllocIds が反映されたフレームで一括適用する。
    const [pendingNotAllocSelect, setPendingNotAllocSelect] = useState<number[] | null>(null);

    // 自動配車などで割り当て済みになった案件は unassigned から消えるので、
    // 選択 state からも除外して「N件選択中」表記を実態に揃える。
    // pending がある間は (= 復元待ち) 厳密フィルタしない (応答到着後の一瞬で消えるのを防ぐ)。
    useEffect(() => {
      if (pendingNotAllocSelect != null) return;
      setSelectedUnassignedIds((prev) => {
        if (prev.size === 0) return prev;
        const validIds = new Set(unassigned.map((u) => u.id));
        let changed = false;
        const next = new Set<string>();
        prev.forEach((id) => {
          if (validIds.has(id)) next.add(id);
          else changed = true;
        });
        return changed ? next : prev;
      });
    }, [unassigned, pendingNotAllocSelect]);

    // pending な notAllocIds が unassigned 側に出揃ったら選択を確定する。
    useEffect(() => {
      if (pendingNotAllocSelect == null) return;
      // 全件割当成功 (notAllocIds 空) なら即時クリアして pending 解除
      if (pendingNotAllocSelect.length === 0) {
        setSelectedUnassignedIds(new Set());
        setPendingNotAllocSelect(null);
        return;
      }
      const targetIds = pendingNotAllocSelect.map((id) => `lr-${id}`);
      const validIds = new Set(unassigned.map((u) => u.id));
      // 期待する notAllocIds が全て unassigned に揃っていなければ次の更新を待つ
      if (!targetIds.every((id) => validIds.has(id))) return;
      setSelectedUnassignedIds(new Set(targetIds));
      setPendingNotAllocSelect(null);
    }, [pendingNotAllocSelect, unassigned]);

    // 重複店舗名・固定ルート外（API 連動は今後）
    const duplicateStoreNames = useMemo<Set<string>>(() => {
      const m = new Map<string, Set<string>>();
      rowGroups.forEach((g) => g.vehicle.cards.forEach((c) => {
        if (c.type !== 'delivery' || c.status === 'no-delivery' || c.status === 'fr-template') return;
        const bucket = m.get(c.storeName);
        if (bucket) bucket.add(g.vehicle.id);
        else m.set(c.storeName, new Set([g.vehicle.id]));
      }));
      return new Set(Array.from(m.entries()).filter(([, v]) => v.size > 1).map(([k]) => k));
    }, [rowGroups]);

    const routeDeviationCardIds = useMemo<Set<string>>(() => {
      const s = new Set<string>();
      if (fixedRouteId == null) return s;
      // 並び替え後 (overrideRowCards 適用後) も判定が崩れないよう、source 側の
      // 「固定ルート外」フラグから legRunId 集合を作り、現在表示中の DispatchCard の
      // legRunIds と突き合わせる。index ベースの対応付けは D&D で順序が変わると壊れるため使わない。
      const deviationLegRunIds = new Set<number>();
      rowGroups.forEach((g) => g.rows.forEach((r) => {
        r.source.cards.forEach((srcCard) => {
          if (srcCard.kind !== 'unloading') return;
          if (!srcCard.isRouteDeviation) return;
          srcCard.legRuns.forEach((lr) => {
            if (lr.id != null) deviationLegRunIds.add(lr.id);
          });
        });
      }));
      if (deviationLegRunIds.size === 0) return s;

      rowGroups.forEach((g) => g.rows.forEach((r) => {
        r.cards.forEach((c) => {
          if (c.type !== 'delivery') return;
          if (c.status === 'no-delivery' || c.status === 'fr-template') return;
          const ids = c.legRunIds ?? [];
          if (ids.some((id) => deviationLegRunIds.has(id))) {
            s.add(c.id);
          }
        });
      }));
      return s;
    }, [rowGroups, fixedRouteId]);

    // 位置情報関連の警告は現状表示しない方針のため空マップを渡す
    const geoAlerts = useMemo(() => new Map<string, GeoAlertInfo>(), []);

    // D&D 設定
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 3 } }));
    const collisionDetection: CollisionDetection = useCallback((args) => {
      const pc = pointerWithin(args);
      if (pc.length > 0) {
        const cardCollision = pc.find((c) => !String(c.id).startsWith('vehicle-row-') && c.id !== 'unassigned');
        return cardCollision ? [cardCollision] : pc;
      }
      return rectIntersection(args);
    }, []);

    const [activeCard, setActiveCard] = useState<DispatchCard | null>(null);
    // 複数選択ドラッグ時のバッジ件数（1件のときは未表示）
    const [dragCount, setDragCount] = useState(0);
    // 他 row / 未割当からのドロップ位置プレビュー (同 row 並び替え時は SortableContext のシフトで分かるので不要)
    type DropPreview = { rowId: string; index: number };
    const [dropPreview, setDropPreview] = useState<DropPreview | null>(null);
    // 現在ドラッグ中のソース所属 rowId (同 row 内の場合はプレビュー表示しない)
    const dragSourceRowIdRef = useRef<string | null>(null);

    // 直近のドラッグドロップで動いたカード id を一定時間ハイライトする (パルスアニメ)。
    // 行内→他行 / 未割当→行 / fr-template リプレイス時に発火。行→未割当 は対象外。
    const [recentlyMovedCardIds, setRecentlyMovedCardIds] = useState<Set<string>>(() => new Set());
    const recentlyMovedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const flashRecentlyMoved = useCallback((ids: string[]) => {
      if (ids.length === 0) return;
      setRecentlyMovedCardIds(new Set(ids));
      if (recentlyMovedTimerRef.current) clearTimeout(recentlyMovedTimerRef.current);
      // route-planner と同じく 1.4s × 2 = 2.8s でフェードアウト
      recentlyMovedTimerRef.current = setTimeout(() => setRecentlyMovedCardIds(new Set()), 2800);
    }, []);
    useEffect(() => () => {
      if (recentlyMovedTimerRef.current) clearTimeout(recentlyMovedTimerRef.current);
    }, []);

    // cardId → {rowId, index} の lookup
    const cardLookup = useMemo(() => {
      const map = new Map<string, { rowId: string; index: number }>();
      rowGroups.forEach((g) => g.rows.forEach((r) => {
        r.cards.forEach((c, idx) => {
          map.set(c.id, { rowId: r.rowId, index: idx });
        });
      }));
      return map;
    }, [rowGroups]);

    const rowLookup = useMemo(() => {
      const map = new Map<string, VehicleRowGroup['rows'][number]>();
      rowGroups.forEach((g) => g.rows.forEach((r) => map.set(r.rowId, r)));
      return map;
    }, [rowGroups]);

    const handleDragStart = useCallback((event: DragStartEvent) => {
      const activeId = String(event.active.id);
      setDropPreview(null);
      // 車両側カード? (複数ドラッグ非対応)
      const loc = cardLookup.get(activeId);
      if (loc) {
        const row = rowLookup.get(loc.rowId);
        if (row) {
          setActiveCard(row.cards[loc.index]);
          setDragCount(0);
          dragSourceRowIdRef.current = row.rowId;
          return;
        }
      }
      // 未割当?: 選択中アイテムなら一括ドラッグ件数を出す
      const u = unassigned.find((i) => i.id === activeId);
      if (u) {
        const isMulti = selectedUnassignedIds.has(activeId) && selectedUnassignedIds.size > 1;
        setDragCount(isMulti ? selectedUnassignedIds.size : 0);
        const card: DeliveryCard = {
          id: u.id,
          type: 'delivery',
          storeName: u.storeName,
          storeAddress: u.storeAddress,
          cases: u.cases,
          todayCases: u.cases,
          status: 'matched',
          isActive: true,
          origin: u.origin,
          destination: u.destination,
          originTimeWindow: u.originTimeWindow,
          destTimeWindow: u.destTimeWindow,
          shippers: u.shippers,
        };
        setActiveCard(card);
        dragSourceRowIdRef.current = null; // 未割当からなので源 row 無し
      }
    }, [cardLookup, rowLookup, unassigned, selectedUnassignedIds]);

    // ドラッグ中: 行 / カード上にホバーしたタイミングで「ここに入る」プレビュー位置を更新する。
    // 同 row 内の並び替えは SortableContext がカードをスライドして可視化するのでプレビュー不要。
    const handleDragOver = useCallback((event: DragOverEvent) => {
      const overId = event.over ? String(event.over.id) : null;
      if (!overId) {
        setDropPreview(null);
        return;
      }
      // -nodrag サフィックス (loading / no-delivery カード) は剥がして lookup
      const lookupId = overId.endsWith('-nodrag') ? overId.slice(0, -'-nodrag'.length) : overId;
      const targetRowId = overId.startsWith('vehicle-row-') ? overId : cardLookup.get(lookupId)?.rowId;
      if (!targetRowId) {
        setDropPreview(null);
        return;
      }
      // 同 row 内ドラッグ中はプレビュー非表示 (SortableContext が並び替え視覚化する)
      if (dragSourceRowIdRef.current && dragSourceRowIdRef.current === targetRowId) {
        setDropPreview(null);
        return;
      }
      const targetRow = rowLookup.get(targetRowId);
      if (!targetRow) {
        setDropPreview(null);
        return;
      }
      const overLoc = cardLookup.get(lookupId);
      const insertIndex = overLoc ? overLoc.index : targetRow.cards.length;
      setDropPreview({ rowId: targetRowId, index: insertIndex });
    }, [cardLookup, rowLookup]);

    const handleDragCancel = useCallback(() => {
      setDropPreview(null);
      dragSourceRowIdRef.current = null;
    }, []);

    const mutateRowCards = useCallback((rowId: string, updater: (cards: DispatchCard[]) => DispatchCard[]) => {
      setOverrideRowCards((prev) => {
        const base = prev.get(rowId) ?? rowLookup.get(rowId)?.cards ?? [];
        const next = new Map(prev);
        next.set(rowId, updater(base));
        return next;
      });
    }, [rowLookup]);

    const handleDragEnd = useCallback((event: DragEndEvent) => {
      const activeId = String(event.active.id);
      const overId = event.over ? String(event.over.id) : null;
      setActiveCard(null);
      setDragCount(0);
      setDropPreview(null);
      dragSourceRowIdRef.current = null;
      if (!overId) return;

      // 1) 未割当 → 車両row (loading/unloading を同地点マージ)
      const fromUnassigned = unassigned.find((u) => u.id === activeId);
      if (fromUnassigned) {
        // loading / no-delivery カードの sortable id は `${card.id}-nodrag` なので、
        // ドロップ先がそれらの場合は -nodrag を剥がして cardLookup で位置を引く。
        const lookupId = overId.endsWith('-nodrag') ? overId.slice(0, -'-nodrag'.length) : overId;
        const targetRowId = overId.startsWith('vehicle-row-') ? overId : cardLookup.get(lookupId)?.rowId;
        if (!targetRowId) return;
        const targetRow = rowLookup.get(targetRowId);
        if (!targetRow) return;

        const overLoc = cardLookup.get(lookupId);

        // 選択中アイテムが複数ある場合は一括移動 (サンプル準拠)
        const isMulti = selectedUnassignedIds.has(activeId) && selectedUnassignedIds.size > 1;
        const idsToMove = isMulti ? Array.from(selectedUnassignedIds) : [activeId];

        const legRunsToMove: ShipmentOrderLegRunForPlanEntity[] = [];
        for (const id of idsToMove) {
          const m = id.startsWith('lr-') ? parseInt(id.slice(3), 10) : NaN;
          const lr = legRuns.find((r) => r.id === m);
          if (lr) legRunsToMove.push(lr);
        }
        if (legRunsToMove.length === 0) return;

        const insertAt = overLoc ? overLoc.index : targetRow.cards.length;

        // setState の updater 内で副作用収集すると React のタイミング (strict mode の二重呼び出し
        // / 遅延適用 等) に依存して移動先 id を取り逃すことがあるため、配置結果は state 設定の
        // 「外側」で計算してから適用する。
        const baseCards = overrideRowCards.get(targetRowId) ?? targetRow.cards;
        let nextCards: DispatchCard[] = baseCards;
        const movedCardIds: string[] = [];
        let currentInsertAt = insertAt;
        for (const lr of legRunsToMove) {
          const before = nextCards.length;
          nextCards = placeLegRunInRow(nextCards, lr, targetRow.rotationNumber, currentInsertAt, targetRow.truckId);
          // 挿入された分だけ次の挿入位置を進める。同地点 unloading にマージされた場合は
          // 配列長は変わらない (= 次もここに刺す) ので before/after 差分を素直に使えば OK。
          const added = nextCards.length - before;
          currentInsertAt += added;
          // 配置先カード (新規 / リプレイス / マージ いずれも) を legRunIds で同定して id 収集
          const placed = nextCards.find(
            (c) => c.type === 'delivery' && lr.id != null && (c.legRunIds ?? []).includes(lr.id),
          );
          if (placed) movedCardIds.push(placed.id);
        }

        mutateRowCards(targetRowId, () => nextCards);
        const moveIdSet = new Set(idsToMove);
        setOverrideUnassigned((prev) => (prev ?? serverUnassigned).filter((u) => !moveIdSet.has(u.id)));
        if (isMulti) setSelectedUnassignedIds(new Set());
        flashRecentlyMoved(movedCardIds);
        return;
      }

      // 2) 車両row → 未割当 (マージ済み legRun は個別の未割当アイテムに展開)
      const sourceLoc = cardLookup.get(activeId);
      if (!sourceLoc) return;
      const sourceRow = rowLookup.get(sourceLoc.rowId);
      const sourceCard = sourceRow?.cards[sourceLoc.index];
      if (!sourceRow || !sourceCard || sourceCard.type !== 'delivery') return;
      // 固定ルート由来の土台カード (fr-template) は本物の案件ではないため移動禁止。
      // DispatchCardView 側で disabled にしているが、念のためここでも弾く (元位置に戻る)。
      if (sourceCard.status === 'fr-template') return;

      if (overId === 'unassigned') {
        const legRunIdsInCard = sourceCard.legRunIds ?? [];
        const newItems: UnassignedItem[] = [];
        if (legRunIdsInCard.length > 0) {
          legRunIdsInCard.forEach((lrId, i) => {
            const lr = legRuns.find((r) => r.id === lrId);
            if (lr) newItems.push(toUnassignedItem(lr, i));
          });
        } else {
          newItems.push({
            id: sourceCard.id,
            storeName: sourceCard.storeName,
            storeAddress: sourceCard.storeAddress,
            cases: sourceCard.todayCases ?? sourceCard.cases,
            origin: sourceCard.origin,
            destination: sourceCard.destination,
            originTimeWindow: sourceCard.originTimeWindow,
            destTimeWindow: sourceCard.destTimeWindow,
            shippers: sourceCard.shippers,
          });
        }
        mutateRowCards(sourceRow.rowId, (cards) => cards.filter((c) => c.id !== activeId));
        setOverrideUnassigned((prev) => [...newItems, ...(prev ?? serverUnassigned)]);
        return;
      }

      // 3) 車両row → 車両row
      // loading / no-delivery カード上にドロップした場合 overId は `${id}-nodrag` なので剥がす
      const lookupId = overId.endsWith('-nodrag') ? overId.slice(0, -'-nodrag'.length) : overId;
      const targetRowId = overId.startsWith('vehicle-row-') ? overId : cardLookup.get(lookupId)?.rowId;
      if (!targetRowId) return;
      const targetRow = rowLookup.get(targetRowId);
      if (!targetRow) return;

      const overLoc = cardLookup.get(lookupId);
      if (sourceRow.rowId === targetRow.rowId) {
        // 同row並び替え
        const insertAt = overLoc ? overLoc.index : targetRow.cards.length - 1;
        if (insertAt === sourceLoc.index) return;
        mutateRowCards(sourceRow.rowId, (cards) => {
          const next = [...cards];
          const [moved] = next.splice(sourceLoc.index, 1);
          next.splice(Math.min(insertAt, next.length), 0, moved);
          return next;
        });
        flashRecentlyMoved([activeId]);
        return;
      }

      // 別row: source から除去し、target に legRun 単位で merge 配置
      const legRunIdsInCard = sourceCard.legRunIds ?? [];
      const insertAtBase = overLoc ? overLoc.index : targetRow.cards.length;
      mutateRowCards(sourceRow.rowId, (cards) => cards.filter((c) => c.id !== activeId));

      // 配置結果は state setter の外で確定させてから適用する (setState updater 内の
      // 副作用収集は strict mode の二重呼び出しで取り逃しになることがあるため)
      const targetBase = overrideRowCards.get(targetRow.rowId) ?? targetRow.cards;
      const movedCardIds: string[] = [];
      let nextTargetCards: DispatchCard[];
      if (legRunIdsInCard.length > 0) {
        let targetCards: DispatchCard[] = targetBase;
        for (const lrId of legRunIdsInCard) {
          const lr = legRuns.find((r) => r.id === lrId);
          if (lr) targetCards = placeLegRunInRow(targetCards, lr, targetRow.rotationNumber, insertAtBase, targetRow.truckId);
          const placed = targetCards.find(
            (c) => c.type === 'delivery' && (c.legRunIds ?? []).includes(lrId),
          );
          if (placed) movedCardIds.push(placed.id);
        }
        nextTargetCards = targetCards;
      } else {
        // legRunId 情報がない場合は従来どおりカードをそのまま挿入
        const next = [...targetBase];
        next.splice(insertAtBase, 0, sourceCard);
        nextTargetCards = next;
        movedCardIds.push(sourceCard.id);
      }
      mutateRowCards(targetRow.rowId, () => nextTargetCards);
      flashRecentlyMoved(movedCardIds);
    }, [
      cardLookup,
      rowLookup,
      mutateRowCards,
      unassigned,
      serverUnassigned,
      legRuns,
      selectedUnassignedIds,
      flashRecentlyMoved,
      overrideRowCards,
    ]);

    const onToggleRightPanel = useCallback(() => setIsRightPanelOpen((prev) => !prev), []);

    // ─────────────────────────────────────────────────────────────
    // 行 (= truck × rotation) → 代表 delivery の shift_truck_id を解決する。
    // 仮想回転 (extraRotations 由来 / operations 未作成の 2 回転目以降) は
    // rowIdToShiftTruckId に登録されていないので、rowId から truckId を取り出して
    // 同 truck の代表 delivery (truckIdToDeliveryId) にフォールバックする。
    // V3Plans CyclePresenter では「cycle 選択 = その delivery が選択された」と扱うのと同等の挙動。
    const resolveShiftTruckIdFromRowId = useCallback((rowId: string): number | null => {
      const direct = rowIdToShiftTruckId.get(rowId);
      if (direct != null) return direct;
      const m = /^vehicle-row-(\d+)-/.exec(rowId);
      if (!m) return null;
      const truckId = parseInt(m[1], 10);
      return truckIdToDeliveryId.get(truckId) ?? null;
    }, [rowIdToShiftTruckId, truckIdToDeliveryId]);

    // 印刷 / 帳票出力 (V3Plans と共通の useReportPrinting hook を利用)
    // ─────────────────────────────────────────────────────────────
    const printTargetShiftIds = useMemo(() => {
      const allShiftIds = deliveries.map((d) => d.shiftId).filter((id): id is number => typeof id === 'number');
      const selectedShiftIds = new Set<number>();
      selectedRowIds.forEach((rowId) => {
        const stid = resolveShiftTruckIdFromRowId(rowId);
        if (stid == null) return;
        const d = deliveries.find((x) => x.id === stid);
        if (d?.shiftId != null) selectedShiftIds.add(d.shiftId);
      });
      return selectedShiftIds.size > 0 ? Array.from(selectedShiftIds) : allShiftIds;
    }, [deliveries, selectedRowIds, resolveShiftTruckIdFromRowId]);
    const reportPrinting = useReportPrinting({
      startOn,
      endOn,
      shiftIds: printTargetShiftIds,
      onAnalyticsEvent: (action, buttonName) =>
        ReactGA.event(action, { screen_name: SCREEN_NAMES.CONVENIENCE_DISPATCHES, button_name: buttonName }),
    });

    // ─────────────────────────────────────────────────────────────
    // 自動配車ボタン: V3Plans / V3Timeline と共通の PlanningPresenter ダイアログを使用する。
    // 「実行する」で V5 normalPlanning mutation を呼び、通常の自動配車 API を走らせる。
    // ─────────────────────────────────────────────────────────────
    const [planningDialogOpen, setPlanningDialogOpen] = useState(false);
    // 移動モード: 詳細ポップの「他車両へ配車」経由でダイアログを開いたときに target 情報を保持する。
    // セット済みのときは planningRequest で transferPlanning を走らせる。
    const [moveTarget, setMoveTarget] = useState<{
      sourceCard: DeliveryCard;
      sourceRowId: string;
      targetRowId: string;
      targetDeliveryId: number;
      targetShiftId: number;
      sourceShiftId: number;
      targetVehicleLabel: string;
      orderIds: number[];
    } | null>(null);
    const relaxedRules = useMemo(() => RelaxedRuleValues.map((it) => it), []);
    const [selectedRelaxedRules, setSelectedRelaxedRules] = useState<RelaxedRuleVo[]>([]);
    const [priorityLargeTrucks, setPriorityLargeTrucks] = useState<number>(0);

    // PlanningPresenter から設定された各種 planning オプション
    const [planningDialogSettings, setPlanningDialogSettings] = useState({
      balancedLoading: false,
      excludeToll: false,
      includeFutureDeliveries: false,
      autoSplitOrders: false,
      dontExchangePreroute: true,
      concurrentAllOrNothing: false,
      expandWorkingStartMinutes: 0,
      expandWorkingEndMinutes: 0,
      expandLoadStartMinutes: 0,
      expandLoadEndMinutes: 0,
      expandUnloadStartMinutes: 0,
      expandUnloadEndMinutes: 0,
      expandMaxVolumeRate: 0,
      expandMaxLoadCapacityRate: 0,
      activateIncrementalMaxNumPistons: false,
      traceMode: false,
      traceOnly: false,
      traceCompanyId: 0,
    });

    const makePlanningDialogBoolUpdater = useCallback(
      (key: keyof typeof planningDialogSettings) => (v: boolean) => {
        setPlanningDialogSettings((s) => ({ ...s, [key]: v }));
      },
      [],
    );
    const makePlanningDialogNumUpdater = useCallback(
      (key: keyof typeof planningDialogSettings) => (v: number) => {
        setPlanningDialogSettings((s) => ({ ...s, [key]: v }));
      },
      [],
    );

    const updateBalancedLoading = useMemo(() => makePlanningDialogBoolUpdater('balancedLoading'), [makePlanningDialogBoolUpdater]);
    const updateExcludeToll = useMemo(() => makePlanningDialogBoolUpdater('excludeToll'), [makePlanningDialogBoolUpdater]);
    const updateIncludeFutureDeliveries = useMemo(() => makePlanningDialogBoolUpdater('includeFutureDeliveries'), [makePlanningDialogBoolUpdater]);
    const updateAutoSplitOrders = useMemo(() => makePlanningDialogBoolUpdater('autoSplitOrders'), [makePlanningDialogBoolUpdater]);
    const updateDontExchangePreroute = useMemo(() => makePlanningDialogBoolUpdater('dontExchangePreroute'), [makePlanningDialogBoolUpdater]);
    const updateConcurrentAllOrNothing = useMemo(() => makePlanningDialogBoolUpdater('concurrentAllOrNothing'), [makePlanningDialogBoolUpdater]);
    const updateExpandWorkingStartMinutes = useMemo(() => makePlanningDialogNumUpdater('expandWorkingStartMinutes'), [makePlanningDialogNumUpdater]);
    const updateExpandWorkingEndMinutes = useMemo(() => makePlanningDialogNumUpdater('expandWorkingEndMinutes'), [makePlanningDialogNumUpdater]);
    const updateExpandLoadStartMinutes = useMemo(() => makePlanningDialogNumUpdater('expandLoadStartMinutes'), [makePlanningDialogNumUpdater]);
    const updateExpandLoadEndMinutes = useMemo(() => makePlanningDialogNumUpdater('expandLoadEndMinutes'), [makePlanningDialogNumUpdater]);
    const updateExpandUnloadStartMinutes = useMemo(() => makePlanningDialogNumUpdater('expandUnloadStartMinutes'), [makePlanningDialogNumUpdater]);
    const updateExpandUnloadEndMinutes = useMemo(() => makePlanningDialogNumUpdater('expandUnloadEndMinutes'), [makePlanningDialogNumUpdater]);
    const updateExpandMaxVolumeRate = useMemo(() => makePlanningDialogNumUpdater('expandMaxVolumeRate'), [makePlanningDialogNumUpdater]);
    const updateExpandMaxLoadCapacityRate = useMemo(() => makePlanningDialogNumUpdater('expandMaxLoadCapacityRate'), [makePlanningDialogNumUpdater]);
    const updateActivateIncrementalMaxNumPistons = useMemo(() => makePlanningDialogBoolUpdater('activateIncrementalMaxNumPistons'), [makePlanningDialogBoolUpdater]);
    const updateTraceMode = useMemo(() => makePlanningDialogBoolUpdater('traceMode'), [makePlanningDialogBoolUpdater]);
    const updateTraceOnly = useMemo(() => makePlanningDialogBoolUpdater('traceOnly'), [makePlanningDialogBoolUpdater]);
    const updateTraceCompanyId = useMemo(() => makePlanningDialogNumUpdater('traceCompanyId'), [makePlanningDialogNumUpdater]);

    // V3Plans / V3Timeline と同じく、必須は「車両」のみ。案件未選択でもダイアログを開く。
    // 案件が選ばれていない場合は、現在ドロップ済みの override 状態をそのまま保存する。
    const autoDispatchDisabledReason = useMemo<string | null>(() => {
      if (selectedRowIds.size === 0) return '配車する車両を選択してください';
      if (isAutoDispatching) return '自動配車を実行中です';
      return null;
    }, [selectedRowIds.size, isAutoDispatching]);

    const handleAutoDispatchClick = useCallback(() => {
      if (autoDispatchDisabledReason !== null) {
        enqueueSnackbar(autoDispatchDisabledReason);
        return;
      }
      setPlanningDialogOpen(true);
    }, [autoDispatchDisabledReason, enqueueSnackbar]);

    const handlePlanningDialogClose = useCallback(() => {
      setPlanningDialogOpen(false);
      setMoveTarget(null);
    }, []);

    // PlanningPresenter は shift_id を要求する（convertToRequestBody → shift_ids に渡る）
    // 選択行 → delivery.id → delivery.shiftId に変換。
    // 仮想回転 (operations 未作成の 2 回転目以降) は rowIdToShiftTruckId に直接無いので
    // resolveShiftTruckIdFromRowId で同 truck の代表 delivery にフォールバックする。
    // 移動モードでは moveTarget から導出した値を優先する。
    const selectedDeliveryIdsForDialog = useMemo(() => {
      if (moveTarget) return [moveTarget.targetShiftId];
      const idSet = new Set<number>();
      selectedRowIds.forEach((rowId) => {
        const stid = resolveShiftTruckIdFromRowId(rowId);
        if (stid == null) return;
        const delivery = deliveries.find((d) => d.id === stid);
        if (delivery?.shiftId != null) idSet.add(delivery.shiftId);
      });
      return Array.from(idSet);
    }, [moveTarget, selectedRowIds, resolveShiftTruckIdFromRowId, deliveries]);

    const selectedOrderIdsForDialog = useMemo(() => {
      if (moveTarget) return moveTarget.orderIds;
      const ids: number[] = [];
      selectedUnassignedIds.forEach((id) => {
        const m = /^lr-(\d+)$/.exec(id);
        if (m) ids.push(parseInt(m[1], 10));
      });
      return ids;
    }, [moveTarget, selectedUnassignedIds]);

    // PlanningPresenter が「実行する」を押したときに呼ぶ関数。
    // 通常モードは normalPlanning、移動モード (moveTarget セット) は transferPlanning を使う。
    const planningRequest = useCallback((shiftIds: number[], orderIds: number[]) => {
      if (shiftIds.length === 0) {
        enqueueSnackbar('配車する車両を選択してください');
        return;
      }
      const planning: PlanningEntity = {
        startOn,
        endOn,
        lockType: 'free',
        isRespectRecent: false,
        excludeToll: planningDialogSettings.excludeToll,
        respectType: 'day_of_week',
        mlPlanning: false,
        respectDaysOfWeek: [],
        respectOn: '',
        isTimeTableBackward: false,
        includeFutureDeliveries: planningDialogSettings.includeFutureDeliveries,
        activateConstraintLoadAfterNotCleared: false,
        loadAfterInitialStock: false,
        setCurrentTimeToAlgorithm: false,
        orderOperationIdsForSort: [],
        deleteOrderIdsFromOperations: [],
        selectedRelaxedRules,
        priorityLargeTrucks,
        expandWorkingStartTime: planningDialogSettings.expandWorkingStartMinutes,
        expandWorkingEndTime: planningDialogSettings.expandWorkingEndMinutes,
        expandLoadStartTime: planningDialogSettings.expandLoadStartMinutes,
        expandLoadEndTime: planningDialogSettings.expandLoadEndMinutes,
        expandUnloadStartTime: planningDialogSettings.expandUnloadStartMinutes,
        expandUnloadEndTime: planningDialogSettings.expandUnloadEndMinutes,
        expandMaxVolumeRate: planningDialogSettings.expandMaxVolumeRate,
        expandMaxLoadCapacityRate: planningDialogSettings.expandMaxLoadCapacityRate,
        requestAsync: true,
        mlSourceType: 0,
        balancedLoading: planningDialogSettings.balancedLoading,
        autoSplitOrders: planningDialogSettings.autoSplitOrders,
        dontExchangePreroute: planningDialogSettings.dontExchangePreroute,
        concurrentAllOrNothing: planningDialogSettings.concurrentAllOrNothing,
        optimizeForLoad: false,
        forceNullBase: false,
        trunkTransportation: false,
        activateIncrementalMaxNumPistons: planningDialogSettings.activateIncrementalMaxNumPistons,
        traceMode: planningDialogSettings.traceMode,
        traceOnly: planningDialogSettings.traceOnly,
        traceCompanyId: planningDialogSettings.traceCompanyId,
      };

      const resultHandlers = {
        onSuccess: (data: AxiosResponse<PlanningResponseEntity>) => {
          if (data.data.status === 'queued') {
            console.log('[convenience-dispatch] planning queued → polling 開始', data.data);
            setAsyncPlanningResponse(data.data);
            // 完了時のキャッシュ操作に使うため、ジョブ開始時の対象日付を記録する。
            runningPlanningRangeRef.current = { startOn, endOn };
          } else {
            console.log('[convenience-dispatch] planning 即時完了', data.data);
            // 即時完了でも、直前の polling 開始 refetch などで running キャッシュに実行中エンティティが
            // 残っている可能性があるため、ジョブ日付 (= 現在の startOn/endOn) でクリアしてフラッシュを防ぐ。
            void clearRunningPlanningsCache(queryClient, startOn, endOn);
            const notAllocIds = data.data.not_allocated_order_ids ?? [];
            // 未割当だった legRun の legRuns キャッシュを assigned=false に書き換えて即時未割当リストに戻す
            // 単体 cache にも prefix match するので pages を持つ infinite query だけを対象にする
            if (notAllocIds.length > 0) {
              const notAllocIdSet = new Set<number>(notAllocIds);
              queryClient.setQueriesData<InfiniteData<ShipmentOrderLegRunListResponse>>(
                { queryKey: [SHIPMENT_ORDER_LEG_RUNS_QUERY_KEY] },
                (old) => {
                  if (!old) return old;
                  const maybePages = (old as { pages?: unknown }).pages;
                  if (!Array.isArray(maybePages)) return old;
                  return {
                    ...old,
                    pages: old.pages.map((p) => ({
                      ...p,
                      data: p.data.map((lr) =>
                        (lr.id != null && notAllocIdSet.has(lr.id)) ? { ...lr, assigned: false } : lr,
                      ),
                    })),
                  };
                },
              );
            }
            hasQueuedOverrideChangeRef.current = false;
            setOverrideRowCards(new Map());
            setOverrideUnassigned(null);
            void queryClient.invalidateQueries({ queryKey: [CONVENIENCE_DISPATCH_DELIVERIES_QUERY_KEY] });
            void queryClient.invalidateQueries({ queryKey: [CONVENIENCE_DISPATCH_ALL_DELIVERIES_QUERY_KEY] });
            void queryClient.invalidateQueries({ queryKey: [SHIPMENT_ORDER_LEG_RUNS_QUERY_KEY] });
            void queryClient.invalidateQueries({ queryKey: ['useQueryLatestAlgorithmRequestVersion'] });
            if (notAllocIds.length > 0 && data.data.session_id) {
              setNotAllocSessionId(data.data.session_id);
              enqueueSnackbar(
                `${notAllocIds.length.toLocaleString()}件が割り当たりませんでした。割当たらない理由を確認してください。`,
              );
            } else {
              setNotAllocSessionId(null);
              enqueueSnackbar('自動配車が完了しました', { variant: 'success' });
            }
          }
        },
        onError: (e: AxiosError<{ message?: string }>) => {
          console.error('[convenience-dispatch] planning エラー', e);
          enqueueSnackbar(e.response?.data?.message ?? '自動配車でエラーが発生しました', { variant: 'error' });
          resetAfterPlanningError();
        },
      };

      if (moveTarget) {
        // 移動モード: V3Plans の transferRequest と同じく、先に対象 legRun を resetting してから transferPlanning を発火する
        enqueueSnackbar(`${moveTarget.orderIds.length.toLocaleString()}件の配送計画を移動します`);
        clearNotAllocHint();
        operationBulkResetting.mutateAsync(moveTarget.orderIds)
          .then(() => {
            transferPlanning.mutate(
              {
                orderIds: moveTarget.orderIds,
                transferToShiftId: moveTarget.targetShiftId,
                transferFromShiftId: moveTarget.sourceShiftId,
                planning,
                latestAlgorithmRequestVersion,
              },
              resultHandlers,
            );
          })
          .catch((e: AxiosError<{ message?: string }>) => {
            enqueueSnackbar(e.response?.data?.message ?? '割当のリセットに失敗しました', { variant: 'error' });
            resetAfterPlanningError();
          });
        setMoveTarget(null);
        return;
      }

      enqueueSnackbar('自動配車を実行中...');
      clearNotAllocHint();
      normalPlanning.mutate(
        {
          shiftIds,
          orderIds,
          planning,
          latestAlgorithmRequestVersion,
          requestAsync: true,
          selectedCycleIndexes: [],
          deliveryOperations: [],
          planningSetting: {
            deliveryConditions: {},
            orderConditions: {},
            planningConditions: {},
          } as unknown as PlanningSettingRequest,
        },
        resultHandlers,
      );

      // 実行開始時点で未割当リストから対象案件を即時削除（V3Plans と同じオプティミスティック更新）。
      // polling 完了後にキャッシュが再取得され、useEffect で override がリセットされる。
      // なお V3Plans/V3Timeline と同じく、選択状態（車両 / 案件）は配車後も保持する。
      if (orderIds.length > 0) {
        const removeIds = new Set(orderIds.map((id) => `lr-${id}`));
        setOverrideUnassigned((prev) => (prev ?? serverUnassigned).filter((u) => !removeIds.has(u.id)));
      }
    }, [
      startOn,
      endOn,
      selectedRelaxedRules,
      priorityLargeTrucks,
      planningDialogSettings,
      normalPlanning,
      transferPlanning,
      operationBulkResetting,
      moveTarget,
      latestAlgorithmRequestVersion,
      enqueueSnackbar,
      queryClient,
      serverUnassigned,
      resetAfterPlanningError,
      clearNotAllocHint,
    ]);

    // 固定ルート反映中は Backdrop で操作ブロック。
    // depot operation も operation_snapshot から復元するため、apply 後の自動配車チェーンは不要。
    const applyChainBusy = isApplying;
    // ツールバー経由の「呼び出し」と未割当パネルの「対応づけ」で文言を分ける (applyMode)
    const applyChainBusyMessage = applyMode === 'call'
      ? '固定ルート呼び出し中...'
      : '固定ルートに対応づけ中...';

    // ─────────────────────────────────────────────────────────────
    // 詳細パネルのアクション（編集 / 他車両へ移動 / 未割当に戻す）
    // ─────────────────────────────────────────────────────────────
    // レーン詳細シート: V2ShipmentOrderLanes と同じ `LaneDetailSheet`（右ドロワー）を再利用する
    const [laneSheetId, setLaneSheetId] = useState<number | null>(null);
    const handleEditLane = useCallback((laneId: number) => {
      // カード詳細ポップアップは閉じてからレーン詳細ドロワーを開く
      closeDetail();
      setLaneSheetId(laneId);
    }, [closeDetail]);
    const handleCloseLaneSheet = useCallback(() => {
      setLaneSheetId(null);
    }, []);

    // 未割当案件の「編集」: /plan/ と同じ案件編集ダイアログ (ShipmentDetail) を開く。
    // unassigned id (= `lr-{legRunId}`) → legRun → shipment_order_id に解決して使う。
    const [editShipmentOrderId, setEditShipmentOrderId] = useState<number | null>(null);
    const handleEditUnassignedOrder = useCallback((shipmentOrderId: number) => {
      // カード詳細ポップアップを閉じてから案件編集ダイアログを開く
      closeDetail();
      setEditShipmentOrderId(shipmentOrderId);
    }, [closeDetail]);
    const handleCloseEditShipmentOrder = useCallback(() => {
      setEditShipmentOrderId(null);
    }, []);

    // V3Plans と同じく、移動先車両選択の直後に「自動配車の設定」ダイアログを開く。
    // 実行する押下時は planningRequest → transferPlanning で配車する。
    const handleMoveToVehicle = useCallback((targetRowId: string) => {
      setMoveMenuAnchor(null);
      if (detailSelection?.source !== 'vehicle' || detailSelection.card.type !== 'delivery') return;
      const sourceCard = detailSelection.card;
      const sourceRow = rowGroups
        .flatMap((g) => g.rows)
        .find((r) => r.cards.some((c) => c.id === sourceCard.id));
      const targetRow = rowLookup.get(targetRowId);
      if (!sourceRow || !targetRow) return;
      if (sourceRow.rowId === targetRow.rowId) return;

      const targetGroup = rowGroups.find((g) => g.rows.some((r) => r.rowId === targetRow.rowId));
      const targetVehicleLabel = targetGroup?.vehicle.vehicleNumber ?? '選択した車両';

      const targetDelivery = deliveries.find((d) => d.id === (rowIdToShiftTruckId.get(targetRow.rowId) ?? -1));
      const sourceDelivery = deliveries.find((d) => d.id === (rowIdToShiftTruckId.get(sourceRow.rowId) ?? -1));
      if (!targetDelivery || !sourceDelivery) {
        enqueueSnackbar('移動先または移動元の delivery が特定できませんでした', { variant: 'error' });
        return;
      }

      const legRunIds = (sourceCard.legRunIds ?? []).filter((n): n is number => typeof n === 'number');
      if (legRunIds.length === 0) {
        enqueueSnackbar('移動対象の案件データが見つかりません', { variant: 'error' });
        return;
      }

      setMoveTarget({
        sourceCard,
        sourceRowId: sourceRow.rowId,
        targetRowId: targetRow.rowId,
        targetDeliveryId: targetDelivery.id,
        targetShiftId: targetDelivery.shiftId,
        sourceShiftId: sourceDelivery.shiftId,
        targetVehicleLabel,
        orderIds: legRunIds,
      });
      // 詳細ポップは閉じて「自動配車の設定」ダイアログだけを前面に出す
      closeDetail();
      setPlanningDialogOpen(true);
    }, [detailSelection, rowGroups, rowLookup, deliveries, rowIdToShiftTruckId, enqueueSnackbar, closeDetail]);

    // 未割当案件の削除 (V3Plans 等で利用されている useMutationShipmentOrder().bulkDelete を再利用)。
    // unassigned id (= `lr-{legRunId}`) → legRun → shipment_order_id にマップ。
    // 同じ shipment_order に複数 legRun があれば 1 つの shipment_order_id にまとまる。
    const { bulkDelete: deleteShipmentOrders } = useMutationShipmentOrder();

    const resolveShipmentOrderIdsFromUnassignedIds = useCallback((ids: string[]): number[] => {
      const set = new Set<number>();
      for (const id of ids) {
        const m = /^lr-(\d+)$/.exec(id);
        if (!m) continue;
        const lrId = Number(m[1]);
        const lr = legRuns.find((r) => r.id === lrId);
        if (lr?.shipment_order_id != null) set.add(lr.shipment_order_id);
      }
      return Array.from(set);
    }, [legRuns]);

    const handleDeleteSelectedUnassigned = useCallback(() => {
      const ids = Array.from(selectedUnassignedIds);
      if (ids.length === 0) return;
      const shipmentOrderIds = resolveShipmentOrderIdsFromUnassignedIds(ids);
      if (shipmentOrderIds.length === 0) {
        enqueueSnackbar('削除対象の案件が見つかりません', { variant: 'error' });
        return;
      }
      // 削除対象 shipment_order 配下の legRun id 全てを unassigned から取り除く必要があるため
      // (1 shipment_order が複数 legRun を持つ場合に備える)
      const shipmentOrderIdSet = new Set(shipmentOrderIds);
      const removedItemIds = new Set<string>();
      legRuns.forEach((lr) => {
        if (lr.id != null && shipmentOrderIdSet.has(lr.shipment_order_id)) {
          removedItemIds.add(`lr-${lr.id}`);
        }
      });
      setConfirmDialog({
        title: '案件を削除しますか？',
        description: `${shipmentOrderIds.length} 件の案件を削除します。元には戻せません。`,
        onConfirm: async () => {
          // 楽観的更新: API レスポンス前に画面から削除対象を消す。
          const prevSelection = new Set(selectedUnassignedIds);
          setOverrideUnassigned((prev) => (prev ?? serverUnassigned).filter((u) => !removedItemIds.has(u.id)));
          setSelectedUnassignedIds((prev) => {
            const next = new Set(prev);
            removedItemIds.forEach((id) => next.delete(id));
            return next;
          });

          try {
            await deleteShipmentOrders.mutateAsync(shipmentOrderIds);
            enqueueSnackbar(`${shipmentOrderIds.length} 件の案件を削除しました`, { variant: 'success' });
            // serverUnassigned 更新を refetch まで待たず、override は useEffect ([sourceRowsRaw, serverUnassigned])
            // が serverUnassigned 変化時に自動クリアするのでここでは何もしない。
          } catch (e) {
            const err = e as AxiosError<{ message: string }>;
            enqueueSnackbar(err.response?.data?.message ?? '削除に失敗しました', { variant: 'error' });
            // 失敗時は override をクリアしてサーバ値に戻し、選択状態も復元する
            setOverrideUnassigned(null);
            setSelectedUnassignedIds(prevSelection);
          }
        },
      });
    }, [
      selectedUnassignedIds, resolveShipmentOrderIdsFromUnassignedIds,
      deleteShipmentOrders, enqueueSnackbar, legRuns, serverUnassigned,
    ]);

    const handleDeleteUnassignedFromDetail = useCallback(() => {
      if (detailSelection?.source !== 'unassigned') return;
      const targetId = detailSelection.item.id;
      const storeName = detailSelection.item.storeName;
      const shipmentOrderIds = resolveShipmentOrderIdsFromUnassignedIds([targetId]);
      if (shipmentOrderIds.length === 0) {
        enqueueSnackbar('削除対象の案件が見つかりません', { variant: 'error' });
        return;
      }
      const shipmentOrderIdSet = new Set(shipmentOrderIds);
      const removedItemIds = new Set<string>();
      legRuns.forEach((lr) => {
        if (lr.id != null && shipmentOrderIdSet.has(lr.shipment_order_id)) {
          removedItemIds.add(`lr-${lr.id}`);
        }
      });
      setConfirmDialog({
        title: '案件を削除しますか？',
        description: `「${storeName}」を削除します。元には戻せません。`,
        onConfirm: async () => {
          // 楽観的更新: 先に画面から消し、ポップアップを閉じる
          const prevSelection = new Set(selectedUnassignedIds);
          setOverrideUnassigned((prev) => (prev ?? serverUnassigned).filter((u) => !removedItemIds.has(u.id)));
          setSelectedUnassignedIds((prev) => {
            const next = new Set(prev);
            removedItemIds.forEach((id) => next.delete(id));
            return next;
          });
          closeDetail();

          try {
            await deleteShipmentOrders.mutateAsync(shipmentOrderIds);
            enqueueSnackbar('案件を削除しました', { variant: 'success' });
          } catch (e) {
            const err = e as AxiosError<{ message: string }>;
            enqueueSnackbar(err.response?.data?.message ?? '削除に失敗しました', { variant: 'error' });
            // 失敗時は元に戻す
            setOverrideUnassigned(null);
            setSelectedUnassignedIds(prevSelection);
          }
        },
      });
    }, [
      detailSelection, resolveShipmentOrderIdsFromUnassignedIds, deleteShipmentOrders,
      enqueueSnackbar, closeDetail, legRuns, serverUnassigned, selectedUnassignedIds,
    ]);

    const handleResetToUnassigned = useCallback(() => {
      if (detailSelection?.source !== 'vehicle' || detailSelection.card.type !== 'delivery') return;
      const sourceCard = detailSelection.card;
      const legRunIds = (sourceCard.legRunIds ?? []).filter((n): n is number => typeof n === 'number');
      if (legRunIds.length === 0) {
        enqueueSnackbar('未割当に戻せる案件データが見つかりません', { variant: 'error' });
        return;
      }
      setConfirmDialog({
        title: '未割り当てに戻しますか？',
        description: `「${sourceCard.storeName}」を外して未割り当てに戻します。よろしいですか？`,
        onConfirm: async () => {
          let apiSucceeded = false;
          try {
            await operationBulkResetting.mutateAsync(legRunIds);
            apiSucceeded = true;
          } catch (e) {
            const err = e as AxiosError<{ message: string }>;
            enqueueSnackbar(err.response?.data?.message ?? '処理に失敗しました', { variant: 'error' });
          }
          if (apiSucceeded) {
            applyResetToCaches(legRunIds);
            enqueueSnackbar(`${legRunIds.length} 件の案件を未割当に戻しました`, { variant: 'success' });
            closeDetail();
          }
        },
      });
    }, [detailSelection, operationBulkResetting, enqueueSnackbar, applyResetToCaches, closeDetail]);

    return (
      <>
        <Helmet>
          <title>配車計画 | カード</title>
        </Helmet>
        <ConvenienceDispatchAddVehicleDialog
          open={addVehicleDialogOpen}
          onClose={() => setAddVehicleDialogOpen(false)}
          startOn={startOn}
          endOn={endOn}
          existingTruckIds={existingTruckIds}
          existingDriverIds={existingDriverIds}
          trucks={trucks}
          drivers={drivers}
        />
        <NotAllocSuggestionComponent
          showNotAllocSuggestionSnackbar={showNotAllocSuggestionSnackbar}
          suggestionDialogOpen={suggestionDialogOpen}
          notAllocSuggestions={notAllocSuggestions}
          handleSuggestionDialogClose={handleSuggestionDialogClose}
          setShowNotAllocSuggestionSnackbar={setShowNotAllocSuggestionSnackbar}
          setSuggestionDialogOpen={setSuggestionDialogOpen}
          // ConvenienceDispatches は V3Plans の actionBar が無いので actionBarHeight 分上に上げる
          snackbarTopOffsetPx={appBarHeight - 15}
        />
        {/* レーン詳細シート（V2ShipmentOrderLanes と同じコンポーネント）
            ConvenienceDispatches のセル編集経由は PM 要望で「基本情報」を最初に開く */}
        <LaneDetailSheet
          laneId={laneSheetId}
          startOn={startOn}
          isOpen={laneSheetId !== null}
          onClose={handleCloseLaneSheet}
          initialTab="basicInfo"
        />
        {/* 未割当案件の「案件編集」ダイアログ（/plan/ と同じ ShipmentDetail を再利用） */}
        {editShipmentOrderId != null && (
          <ShipmentDetail
            // 案件 ID が変わったら確実に再マウントして内部状態 (isEditing 等) をリセットする
            key={editShipmentOrderId}
            shipmentId={editShipmentOrderId}
            editShipmentDialogOpen
            onClose={handleCloseEditShipmentOrder}
            startOn={startOn}
            editOnOpen
          />
        )}
        <Dialog
          open={planningDialogOpen}
          onClose={handlePlanningDialogClose}
          fullWidth
          maxWidth="md"
        >
          <PlanningPresenter
            screenName="convenience_dispatch"
            updateBalancedLoading={updateBalancedLoading}
            updateExcludeToll={updateExcludeToll}
            updateIncludeFutureDeliveries={updateIncludeFutureDeliveries}
            updateAutoSplitOrders={updateAutoSplitOrders}
            updateDontExchangePreroute={updateDontExchangePreroute}
            updateConcurrentAllOrNothing={updateConcurrentAllOrNothing}
            updateExpandWorkingStartMinutes={updateExpandWorkingStartMinutes}
            updateExpandWorkingEndMinutes={updateExpandWorkingEndMinutes}
            updateExpandLoadStartMinutes={updateExpandLoadStartMinutes}
            updateExpandLoadEndMinutes={updateExpandLoadEndMinutes}
            updateExpandUnloadStartMinutes={updateExpandUnloadStartMinutes}
            updateExpandUnloadEndMinutes={updateExpandUnloadEndMinutes}
            updateExpandMaxVolumeRate={updateExpandMaxVolumeRate}
            updateExpandMaxLoadCapacityRate={updateExpandMaxLoadCapacityRate}
            updateActivateIncrementalMaxNumPistons={updateActivateIncrementalMaxNumPistons}
            updateTraceMode={updateTraceMode}
            updateTraceOnly={updateTraceOnly}
            updateTraceCompanyId={updateTraceCompanyId}
            planningRequest={planningRequest}
            onClose={handlePlanningDialogClose}
            selectedDeliveryIds={selectedDeliveryIdsForDialog}
            selectedOrderIds={selectedOrderIdsForDialog}
            startOn={startOn}
            relaxedRules={relaxedRules}
            selectedRelaxedRules={selectedRelaxedRules}
            setSelectedRelaxedRules={setSelectedRelaxedRules}
            setPriorityLargeTrucks={setPriorityLargeTrucks}
            priorityLargeTrucks={priorityLargeTrucks}
          />
        </Dialog>
        <Box sx={{ display: 'flex', flexDirection: 'column', height: `calc(100vh - ${appBarHeight}px)`, overflow: 'hidden' }}>
          <ConvenienceDispatchToolbar
            startOn={startOn}
            endOn={endOn}
            fixedRouteId={fixedRouteId}
            fixedRoutes={fixedRoutes}
            isLoadingFixedRoutes={isLoadingFixedRoutes}
            isApplying={isApplying}
            onDateRangeChange={onDateRangeChange}
            onFixedRouteSelect={onFixedRouteSelect}
            onFixedRouteCreated={onFixedRouteCreated}
            onFixedRouteCleared={onFixedRouteCleared}
            onApplyFixedRoute={onApplyFixedRoute}
            displayMetrics={displayMetrics}
            onDisplayMetricsChange={setDisplayMetrics}
            onNavigate={onNavigate}
            visibleColumns={visibleColumns}
            onVisibleColumnsChange={setVisibleColumns}
            selectorDialogOpen={fixedRouteSelectorOpen}
            onSelectorDialogOpenChange={setFixedRouteSelectorOpen}
            onAutoDispatch={handleAutoDispatchClick}
            isAutoDispatchDisabled={isAutoDispatching}
            autoDispatchTooltip={autoDispatchDisabledReason ?? undefined}
          />

          <DndContext
            sensors={sensors}
            collisionDetection={collisionDetection}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragCancel={handleDragCancel}
            onDragEnd={handleDragEnd}
          >
            <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
              {/* 左+中央ラッパー: 外側Yスクロールで左右を一緒にスクロール */}
              <Box sx={{ flex: 1, overflowY: 'auto' }}>
                <Box sx={{ display: 'flex', minHeight: '100%', minWidth: 0 }}>
                  {/* 左カラム: 自前スクロールなし。外側Yに同乗する */}
                  <Box
                    sx={{
                      width: 304,
                      flexShrink: 0,
                      borderRight: 1,
                      borderColor: 'divider',
                      bgcolor: 'background.paper',
                    }}
                  >
                    <ConvenienceDispatchVehicleList
                      canSortTruckDisplayOrder={canSortTruckDisplayOrder}
                      isMutatingTruckOrder={truckDisplayOrderMutation.isLoading}
                      onTruckOrderChange={handleTruckOrderChange}
                      rowGroups={filteredRowGroups}
                      isLoading={deliveriesQuery.isLoading}
                      displayMetrics={displayMetricSet}
                      selectedRowIds={selectedRowIds}
                      allSelected={allSelected}
                      truckDisplayStatus={truckDisplayStatus}
                      onTruckDisplayStatusChange={setTruckDisplayStatus}
                      hasSelection={selectedRowIds.size > 0}
                      truckGroupEntitiesWithAll={truckGroupEntitiesWithAll}
                      selectedTruckGroupIndices={selectedTruckGroupIndices}
                      onSelectedTruckGroupIndicesChange={setSelectedTruckGroupIndices}
                      onRowToggle={onRowToggle}
                      onSelectAll={onSelectAll}
                      onDeselectAll={onDeselectAll}
                      onUnassignSelected={handleUnassignSelected}
                      onAddVehicle={() => setAddVehicleDialogOpen(true)}
                      onToggleVehicleEnabled={handleToggleVehicleEnabled}
                      onAddRotation={handleAddRotation}
                      onDeleteRotation={handleDeleteRotation}
                      onTruckEdit={handleTruckEdit}
                      onDriverEdit={handleDriverEdit}
                      onPlateNumberEdit={handlePlateNumberEdit}
                      onOpenPrint={reportPrinting.open}
                    />
                  </Box>

                  {/* 中央カラム: X方向のみ自前スクロール。sticky top のヘッダは外側Yに吸着 */}
                  <Box sx={{ minWidth: 0, flex: 1, overflowX: 'auto', overflowY: 'visible' }}>
                    <ConvenienceDispatchBoard
                      rowGroups={filteredRowGroups}
                      isLoading={deliveriesQuery.isLoading}
                      visibleColumns={visibleColumns}
                      displayMetrics={displayMetricSet}
                      selectedCardIds={selectedCardIds}
                      duplicateStoreNames={duplicateStoreNames}
                      routeDeviationCardIds={routeDeviationCardIds}
                      geoAlerts={geoAlerts}
                      hasFixedRoutes={fixedRoutes.length > 0}
                      hasAnyDelivery={rowGroups.length > 0}
                      dropPreview={dropPreview}
                      recentlyMovedCardIds={recentlyMovedCardIds}
                      onSelectCard={onSelectCard}
                    />
                  </Box>
                </Box>
              </Box>

              {/* 右カラム: 左上に張り出すトグルボタン付き */}
              <Box sx={{ position: 'relative', display: 'flex', height: '100%', minHeight: 0, flexShrink: 0 }}>
                {/* Toggle button: 右パネルの左端に半分はみ出す円ボタン */}
                <Box
                  component="button"
                  type="button"
                  onClick={onToggleRightPanel}
                  aria-label={isRightPanelOpen ? '右パネルを閉じる' : '右パネルを開く'}
                  sx={{
                    position: 'absolute',
                    left: -12,
                    top: 12,
                    zIndex: 40,
                    display: 'flex',
                    height: 24,
                    width: 24,
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '9999px',
                    border: '1px solid hsl(var(--border))',
                    bgcolor: 'hsl(var(--background))',
                    boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
                    cursor: 'pointer',
                    transition: 'background-color 0.15s',
                    '&:hover': { bgcolor: 'hsl(var(--accent))' },
                  }}
                >
                  <Box
                    component="svg"
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    sx={{
                      color: 'hsl(var(--muted-foreground))',
                      transition: 'transform 0.2s',
                      transform: isRightPanelOpen ? 'rotate(0deg)' : 'rotate(180deg)',
                    }}
                  >
                    <polyline points="9 18 15 12 9 6" />
                  </Box>
                </Box>

                <Box
                  sx={{
                    height: '100%',
                    minHeight: 0,
                    borderLeft: 1,
                    borderColor: 'divider',
                    transition: 'width 0.2s',
                    overflow: 'hidden',
                    width: isRightPanelOpen ? 380 : 0,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <ConvenienceDispatchUnassignedList
                    items={unassigned}
                    rowGroups={rowGroups}
                    deliveries={enrichedDeliveries}
                    isLoading={legRunsQuery.isLoading}
                    displayMetrics={displayMetricSet}
                    selectedItemIds={selectedUnassignedIds}
                    onSelectItem={onSelectUnassigned}
                    onShowDetail={(item, rect) => {
                      setDetailClickRect({
                        top: rect.top,
                        left: rect.left,
                        right: rect.right,
                        width: rect.width,
                        height: rect.height,
                      });
                      setDetailSelection({ source: 'unassigned', item });
                    }}
                    notAllocReasonsByOrderId={notAllocReasonsByOrderId}
                    notAllocDeliveries={notAllocDeliveriesForReason}
                    notAllocTrucks={trucks}
                    notAllocDrivers={drivers}
                    // 固定ルートが選択されているときだけ「固定ルートに対応づけ」ボタンを有効化する
                    // クリック時は確認ダイアログを挟んでから実行する（サンプル準拠）
                    onAlignToFixedRoute={
                      fixedRouteId != null
                        ? () => {
                            const currentRoute = fixedRoutes.find((r) => r.id === fixedRouteId);
                            const routeName = currentRoute?.name ?? '選択中の固定ルート';
                            setConfirmDialog({
                              title: '出荷データを固定ルートに対応づけ',
                              description: `「${routeName}」に現在の出荷データ（${unassigned.length}件）を対応づけます。よろしいですか？`,
                              onConfirm: () => {
                                onAlignToFixedRoute(fixedRouteId);
                              },
                            });
                          }
                        : undefined
                    }
                    isAligningToFixedRoute={isApplying}
                    onDeleteSelected={handleDeleteSelectedUnassigned}
                    onRestoreSplit={handleRestoreSplit}
                  />
                </Box>
              </Box>
            </Box>

            <DragOverlay>
              {activeCard && (
                <Box sx={{ position: 'relative' }}>
                  <DispatchCardView card={activeCard} isDragging displayMetrics={displayMetricSet} />
                  {dragCount > 1 && (
                    <Box
                      component="span"
                      sx={{
                        position: 'absolute',
                        right: -8,
                        top: -8,
                        display: 'flex',
                        height: 20,
                        width: 20,
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '9999px',
                        bgcolor: 'hsl(var(--primary))',
                        fontSize: 10,
                        fontWeight: 700,
                        color: 'hsl(var(--primary-foreground))',
                        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                      }}
                    >
                      {dragCount}
                    </Box>
                  )}
                </Box>
              )}
            </DragOverlay>
          </DndContext>

          {/* カード詳細ポップオーバー (route-planner の DispatchDetailPanel) */}
          {detailOpen && detailSelection && (() => {
            const popoverWidth = 380;
            const gap = 8;
            const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1200;
            const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 800;
            const posStyle: React.CSSProperties = {};
            if (detailClickRect) {
              const cellCenterY = detailClickRect.top + detailClickRect.height / 2;
              const spaceRight = viewportWidth - detailClickRect.right;
              const showOnLeft = spaceRight < popoverWidth + gap + 40;
              if (showOnLeft) {
                posStyle.right = viewportWidth - detailClickRect.left + gap;
              } else {
                posStyle.left = detailClickRect.right + gap;
              }
              const maxHeight = Math.min(viewportHeight * 0.8, 600);
              let top = cellCenterY - maxHeight / 2;
              top = Math.max(16, Math.min(top, viewportHeight - maxHeight - 16));
              posStyle.top = top;
              posStyle.maxHeight = maxHeight;
            } else {
              posStyle.right = 360;
              posStyle.top = '50%';
              posStyle.transform = 'translateY(-50%)';
              posStyle.maxHeight = '80vh';
            }
            const selectedDeliveryCard =
              detailSelection.source === 'vehicle' && detailSelection.card.type === 'delivery'
                ? detailSelection.card
                : null;
            const hasDuplicate = selectedDeliveryCard
              ? duplicateStoreNames.has(selectedDeliveryCard.storeName)
              : false;
            const selectedId =
              detailSelection.source === 'vehicle'
                ? detailSelection.card.id
                : detailSelection.item.id;
            const geoAlert = geoAlerts.get(selectedId);
            return (
              <Box
                sx={{ position: 'fixed', inset: 0, zIndex: 1300 }}
                onClick={closeDetail}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    width: popoverWidth,
                    overflowY: 'auto',
                    borderRadius: '12px',
                    border: '1px solid hsl(var(--border))',
                    bgcolor: 'hsl(var(--background))',
                    boxShadow: '0 25px 50px -12px rgb(0 0 0 / 0.25)',
                  }}
                  style={posStyle}
                  onClick={(e: React.MouseEvent) => e.stopPropagation()}
                >
                  <Box
                    sx={{
                      position: 'sticky',
                      top: 0,
                      zIndex: 10,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      gap: 0.5,
                      px: 1.5,
                      pt: 1.5,
                      pb: 0.5,
                      bgcolor: 'hsl(var(--background))',
                    }}
                  >
                    {detailSelection.source === 'unassigned' ? (() => {
                      // IIFE 境界を越えると型絞り込みが維持されないため、明示的に narrow する
                      if (detailSelection.source !== 'unassigned') return null;
                      // 未割当案件は /plan/ と同じく「案件編集」ダイアログを開く
                      const shipmentOrderId =
                        resolveShipmentOrderIdsFromUnassignedIds([detailSelection.item.id])[0] ?? null;
                      const canEdit = shipmentOrderId != null;
                      const tooltip = canEdit
                        ? '案件編集画面を開く'
                        : 'この案件は編集できません';
                      return (
                        <Tooltip title={tooltip} arrow>
                          <span>
                            <IconButton
                              size="small"
                              onClick={canEdit ? () => handleEditUnassignedOrder(shipmentOrderId) : undefined}
                              aria-label="案件編集を開く"
                              disabled={!canEdit}
                            >
                              <EditOutlined sx={{ fontSize: 16 }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      );
                    })() : (() => {
                      // IIFE 境界を越えると型絞り込みが維持されないため、明示的に narrow する
                      if (detailSelection.source !== 'vehicle') return null;
                      const laneId = detailSelection.card.laneId;
                      const canEdit = laneId != null;
                      const tooltip = canEdit
                        ? 'レーンの編集画面を開く'
                        : 'この案件は対応するレーンがありません';
                      return (
                        <Tooltip title={tooltip} arrow>
                          <span>
                            <IconButton
                              size="small"
                              onClick={canEdit ? () => handleEditLane(laneId) : undefined}
                              aria-label="レーン管理を開く"
                              disabled={!canEdit}
                            >
                              <EditOutlined sx={{ fontSize: 16 }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      );
                    })()}
                    {detailSelection.source === 'vehicle' && detailSelection.card.type === 'delivery' && (
                      <>
                        <Tooltip title="他車両へ配車" arrow>
                          <IconButton
                            size="small"
                            onClick={(e) => setMoveMenuAnchor(e.currentTarget)}
                            aria-label="他車両へ配車"
                          >
                            <SwapHorizRounded sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                        <Popover
                          open={Boolean(moveMenuAnchor)}
                          anchorEl={moveMenuAnchor}
                          onClose={() => setMoveMenuAnchor(null)}
                          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                          slotProps={{ paper: { sx: { width: 240, maxHeight: 320, overflowY: 'auto', p: 0.5 } } }}
                        >
                          <Typography variant="caption" sx={{ px: 1, py: 0.75, display: 'block', color: 'text.secondary' }}>
                            移動先の車両を選択
                          </Typography>
                          {rowGroups
                            .flatMap((g) => g.rows.map((r) => ({ group: g, row: r })))
                            .filter(({ row }) => row.rowId !== (() => {
                              const sel = detailSelection;
                              if (sel?.source !== 'vehicle') return null;
                              const r = rowGroups
                                .flatMap((g) => g.rows)
                                .find((rr) => rr.cards.some((c) => c.id === sel.card.id));
                              return r?.rowId;
                            })())
                            .map(({ group, row }) => (
                              <MenuItem
                                key={row.rowId}
                                onClick={() => handleMoveToVehicle(row.rowId)}
                                sx={{ fontSize: 13 }}
                              >
                                <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                                  <Box sx={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {group.vehicle.vehicleNumber}
                                    {row.totalRotations > 1 ? ` (${row.rotationNumber}回転目)` : ''}
                                  </Box>
                                  <Box sx={{ fontSize: 11, color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {group.vehicle.driverName}
                                  </Box>
                                </Box>
                              </MenuItem>
                            ))}
                        </Popover>
                        <Tooltip title="未割り当てに戻す" arrow>
                          <IconButton
                            size="small"
                            onClick={() => {
                              ReactGA.event('click', {
                                screen_name: SCREEN_NAMES.CONVENIENCE_DISPATCHES,
                                button_name: '未割当に戻す',
                              });
                              handleResetToUnassigned();
                            }}
                            aria-label="未割り当てに戻す"
                          >
                            <RestartAltRounded sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    {detailSelection.source === 'unassigned' && (
                      <>
                        {/* 分割済みの案件は再分割できない（地図画面と同様）。復元してから分割する */}
                        {!detailSelection.item.wasSplit && (
                          <Tooltip title="伝票を分割" arrow>
                            <IconButton
                              size="small"
                              onClick={handleOpenSplitDialog}
                              aria-label="伝票を分割"
                            >
                              <CallSplitRoundedIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="案件を削除" arrow>
                          <IconButton
                            size="small"
                            onClick={() => {
                              ReactGA.event('click', {
                                screen_name: SCREEN_NAMES.CONVENIENCE_DISPATCHES,
                                button_name: '未割当案件 削除',
                              });
                              handleDeleteUnassignedFromDetail();
                            }}
                            aria-label="案件を削除"
                            sx={{ color: 'hsl(var(--destructive))' }}
                          >
                            <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    <IconButton size="small" onClick={closeDetail} aria-label="閉じる">
                      <CloseRounded sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                  <Box sx={{ px: 2.5, pb: 2.5 }}>
                    <DispatchDetailPanel
                      selection={detailSelection}
                      hasDuplicate={hasDuplicate}
                      geoAlert={geoAlert}
                      onGeoUpdate={handleGeoUpdate}
                    />
                  </Box>
                </Box>
              </Box>
            );
          })()}

          {/* 共通確認ダイアログ */}
          <ConfirmDialog
            open={confirmDialog !== null}
            title={confirmDialog?.title}
            message={confirmDialog?.description ?? ''}
            handleCancel={() => setConfirmDialog(null)}
            handleOk={() => {
              const cb = confirmDialog?.onConfirm;
              setConfirmDialog(null);
              if (cb) void cb();
            }}
          />

          {/* 帳票出力 (V3Plans と共通) */}
          <ReportPrintingDialogs {...reportPrinting.dialogProps} />

          {/* 未割当案件の手動分割ダイアログ */}
          <ShipmentOrderLegRunSplitFormPresenter
            open={splitDialogOpen}
            legRun={splitTargetLegRun}
            onClose={closeSplitDialog}
            onSuccess={handleSplitSuccess}
          />

          {/* 車両 / ドライバー / 車番 編集メニュー (左リストから開く) */}
          <TruckSelectMenu
            anchorEl={truckEditState?.anchor ?? null}
            onClose={() => setTruckEditState(null)}
            onSelect={(t) => { void handleTruckSelect(t.id); }}
            trucks={trucks}
            excludeTruckIds={usedTruckIdsByOtherRows}
            currentTruckId={truckEditState?.truckId ?? null}
          />
          <DriverSelectMenu
            anchorEl={driverEditState?.anchor ?? null}
            onClose={() => setDriverEditState(null)}
            onSelect={(d) => { void handleDriverSelect(d.id); }}
            drivers={drivers}
            excludeDriverIds={usedDriverIdsByOtherRows}
            currentDriverId={driverEditState ? findVehicleByTruckId(driverEditState.truckId)?.driverId ?? null : null}
          />
          <Popover
            open={Boolean(plateEditState)}
            anchorEl={plateEditState?.anchor ?? null}
            onClose={() => setPlateEditState(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1 }}>
              <TextField
                size="small"
                value={plateInputValue}
                onChange={(e) => setPlateInputValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); void handlePlateNumberSubmit(); }
                  if (e.key === 'Escape') { setPlateEditState(null); }
                }}
                inputRef={(el: HTMLInputElement | null) => { el?.focus(); }}
                placeholder="車番"
                sx={{ width: 180 }}
              />
              <Button size="small" variant="contained" onClick={() => { void handlePlateNumberSubmit(); }}>
                保存
              </Button>
            </Box>
          </Popover>
        </Box>

        {/* 固定ルート適用 → 自動配車の連続フロー中は画面操作をブロック */}
        <Backdrop
          open={applyChainBusy}
          sx={{
            zIndex: (theme) => theme.zIndex.drawer + 1,
            color: '#fff',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <CircularProgress color="inherit" />
          <Typography variant="body1">{applyChainBusyMessage}</Typography>
        </Backdrop>

        {/* 自動配車 / 未割当に戻す 実行中は右下にトーストでフィードバック。
            操作直後 (大量オーダー時) に挙動が無く見えないよう、進行中であることを伝える。
            applyChainBusy で Backdrop が出ている時は重複表示を避けるため非表示。 */}
        {!applyChainBusy && (isAutoDispatching || operationBulkResetting.isLoading) && (
          <Box
            role="status"
            aria-live="polite"
            sx={{
              position: 'fixed',
              bottom: 24,
              right: 24,
              zIndex: (theme) => theme.zIndex.snackbar + 10,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              px: 2,
              py: 1.25,
              borderRadius: 2,
              bgcolor: 'background.paper',
              color: 'text.primary',
              border: 1,
              borderColor: 'divider',
              boxShadow: '0 10px 30px -10px rgba(0,0,0,0.4)',
              minWidth: 240,
              animation: 'progressToastSlideUp 0.2s ease-out',
              '@keyframes progressToastSlideUp': {
                from: { opacity: 0, transform: 'translateY(8px)' },
                to: { opacity: 1, transform: 'translateY(0)' },
              },
            }}
          >
            <CircularProgress size={20} thickness={4} color="primary" />
            <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {operationBulkResetting.isLoading ? '未割当に戻しています…' : '自動配車を計算中…'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                しばらくお待ちください
              </Typography>
            </Box>
          </Box>
        )}
      </>
    );
  },
);

export default ConvenienceDispatchesPresenter;
